import React, { useState, useEffect, useMemo, lazy, Suspense, useCallback, useRef } from 'react';
import { toast } from './components/Toast';
import { assertCanDispatchOrder } from './utils/paymentPolicy';
import { omitServerOnlyStoreFields } from './utils/storeSavePayload';
import { 
  Role, Product, CartItem, Order, Vendor, VendorCategory,
  Driver, Client, Language, UserLocation, ProductCustomization, Supplier
} from './types';
import { APP_NAME, DELIVERY_CONFIG } from './constants';
import { authService } from './services/authService';
import { auth, db } from './firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, query, where, limit, getDocs, collection } from 'firebase/firestore';
import { acceptOrderTransaction, completeOrderTransaction } from './services/orderTransactions';
import { PublicAgoraPage } from './components/PublicAgoraPage';
import { RegisterModalOverlay } from './components/RegisterModalOverlay';
import { isDashboardRole, removeOrphanOverlays, releaseBodyScrollLock } from './utils/modalOverlay';
import {
  clearCartSession,
  loadCartSession,
  saveCartSession,
} from './utils/checkoutSession';
import { getClientDefaultDeliveryMode } from './utils/deliveryModePolicyClient';

// ============================================
// CODE-SPLITTING: Dashboards carregados sob demanda
// ============================================
const VendorDashboard = lazy(() =>
  import('./components/VendorDashboardV2').then(
    m => ({ default: m.VendorDashboard })
  )
);
const CityHallDashboard = lazy(() => import('./components/CityHallDashboard').then(m => ({ default: m.CityHallDashboard })));
const MunicipalGovDashboard = lazy(() =>
  import('./components/govtech/MunicipalGovDashboard').then((m) => ({ default: m.MunicipalGovDashboard }))
);
const DriverDashboard = lazy(() => import('./components/DriverDashboard').then(m => ({ default: m.DriverDashboard })));
const SupplierDashboard = lazy(() => import('./components/SupplierDashboard').then(m => ({ default: m.SupplierDashboard })));
const OwnerDashboard = lazy(() => import('./components/OwnerDashboard').then(m => ({ default: m.OwnerDashboard })));
const PublicPixKeyPage = lazy(() => import('./components/PublicPixKeyPage').then(m => ({ default: m.PublicPixKeyPage })));
const MobileRegistrationFlow = lazy(() =>
  import('./components/registration/MobileRegistrationFlow').then((m) => ({
    default: m.MobileRegistrationFlow,
  }))
);
const AIAssistant = lazy(() => import('./components/AIAssistant').then(m => ({ default: m.AIAssistant })));

// Componentes carregados sob demanda (não bloqueiam first paint)
const CartDrawer = lazy(() => import('./components/CartDrawer').then(m => ({ default: m.CartDrawer })));
const VendorStorePage = lazy(() => import('./components/VendorStorePage').then(m => ({ default: m.VendorStorePage })));
const SupplierStorePage = lazy(() => import('./components/SupplierStorePage').then(m => ({ default: m.SupplierStorePage })));
const ProductCustomizationModal = lazy(() => import('./components/ProductCustomizationModal').then(m => ({ default: m.ProductCustomizationModal })));
const LandingPage = lazy(() => import('./components/LandingPage').then(m => ({ default: m.LandingPage })));
const CreditPreviewPanel = lazy(() =>
  import('./components/CreditPreviewPanel').then((m) => ({ default: m.CreditPreviewPanel }))
);

import { Button } from './components/Button';
import { AppErrorBoundary } from './components/ErrorBoundary';
import SupplierCard from './components/SupplierCard';
import { AppProvider, useApp } from './contexts/AppContext';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useResponsiveScroll } from './hooks/useResponsiveScroll';
import { setAudioEnabled } from './utils/audio';
import { validateStrongPassword } from './utils/validators';
import { normalizeRegionalId } from './utils/vendorIdentity';
import { resolveSupplierImage } from './utils/resolveSupplierImage';
import { isVendorStoreLive, VENDOR_STORE_CLOSED_MESSAGE, isPublicListingVendor, sortVendorsOpenFirst } from './utils/vendorPresence';
import { triggerGeocodeVendorAddress } from './services/geocodeVendorService';
import { resolveStoreSlug, claimSlug, releaseSlug } from './services/storeSlugService';
import { isStoreOpen, logStorePresenceCheck } from './utils/storePresence';
import { SUPPLIER_STORE_CLOSED_MESSAGE } from './utils/supplierPresence';
import { getConfirmedPixDisplayKey, getPixPayloadKey } from './utils/pixKeyNormalize';
import { useScopedProducts } from './hooks/useScopedProducts';
import {
  resolveStoreSections,
  useStoreSectionCatalog,
} from './hooks/useStoreSectionCatalog';
import {
  mergeSectionLists,
  useVendorCategorySections,
} from './hooks/useVendorCategorySections';
import { enrichProductL6Fields } from './utils/enrichProductL6Fields';
import {
  resolveVendorByAuthUid,
  resolveEffectiveUserRole,
  syncUserRoleIfVendor,
  vendorMatchesAuthUid,
} from './utils/resolveVendorByAuthUid';
import { audioService } from './services/audioService';
import { 
  User, ShoppingBag, X, ArrowLeft, Loader2, ShieldCheck, Lock, Settings, LogOut, AlertTriangle,
  UtensilsCrossed, Shirt, Palette, Gift, Bike, Dumbbell, Wrench, Package, Factory
} from 'lucide-react';
import { formatBRLNumber } from './utils/formatBRL';

// Lazy: renderizados apenas condicionalmente — não bloqueiam carregamento inicial
const RegistrationScreen = lazy(() => import('./components/RegistrationScreen').then(m => ({ default: m.RegistrationScreen })));
const LoginModal         = lazy(() => import('./components/LoginModal').then(m => ({ default: m.LoginModal })));
const OrderSuccess       = lazy(() => import('./components/OrderSuccess').then(m => ({ default: m.OrderSuccess })));
const PublicOrderTrackingPage = lazy(() => import('./components/PublicOrderTrackingPage').then(m => ({ default: m.PublicOrderTrackingPage })));
const RatingModal        = lazy(() => import('./components/RatingModal').then(m => ({ default: m.RatingModal })));
const AccountSettings    = lazy(() => import('./components/AccountSettings').then(m => ({ default: m.AccountSettings })));

// ============================================
// LOGGER CONDICIONAL (sem logs em produção)
// ============================================
const IS_PRODUCTION = import.meta.env.PROD;
const DEBUG = !IS_PRODUCTION && import.meta.env.DEV;
const log = {
  info: (...args: unknown[]) => DEBUG && console.log(...args),
  warn: (...args: unknown[]) => DEBUG && console.warn(...args),
  error: (...args: unknown[]) => console.error(...args),
};

// Loading fallback para lazy components
const DashboardLoader = () => (
  <div className="flex items-center justify-center h-screen bg-gray-50">
    <div className="text-center">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
      <p className="text-gray-600">Carregando...</p>
    </div>
  </div>
);

// Remove `undefined` em profundidade só em objetos plain (evita estragar Date, Timestamp, etc.)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === null || proto === Object.prototype;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const removeUndefined = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (!isPlainObject(obj)) return obj;
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, removeUndefined(value)])
  );
};

type ViewType = 'home' | 'directory' | 'store' | 'suppliers' | 'supplier-store' | 'professional' | 'agora';

const AGORA_URL_PARAM = 'agora';
const AGORA_HIST_KEY = 'eambAgora';
/** Rota isolada — demonstração fictícia (LGPD). */
const CREDIT_PREVIEW_PATH = '/credit-preview';
const CREDIT_PREVIEW_HIST_KEY = 'eambCreditPreview';
/** Rota isolada — painel real (OWNER / SUPER_ADMIN). */
const PARTNER_CREDIT_PATH = '/partner-credit';
const PARTNER_CREDIT_HIST_KEY = 'eambPartnerCredit';

function isCreditPreviewPathname(pathname?: string): boolean {
  const p = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return /^\/credit-preview\/?$/.test(p);
}

function isPixKeyInvitePathname(pathname?: string): boolean {
  const p = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return /^\/pix-chave\/?$/.test(p);
}

/** Cadastro mobile-first — /v ou /v/:campaign (flag VITE_MOBILE_REGISTER_ENABLED). */
function isMobileRegisterPathname(pathname?: string): boolean {
  const p = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return /^\/v(\/[^/?#]+)?\/?$/.test(p) || /^\/cadastrar(\/[^/?#]+)?\/?$/.test(p);
}

function parseMobileRegisterCampaign(pathname?: string): string | null {
  const p = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const m = p.match(/^\/(?:v|cadastrar)\/([^/?#]+)\/?$/);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

function isMobileRegisterEnabledClient(): boolean {
  return String(import.meta.env.VITE_MOBILE_REGISTER_ENABLED || '').toLowerCase() === 'true';
}

function isPartnerCreditPathname(pathname?: string): boolean {
  const p = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return /^\/partner-credit\/?$/.test(p);
}

function buildPathWithSearch(pathname: string, params: URLSearchParams, hash = ''): string {
  const qs = params.toString();
  const base = pathname + (qs ? `?${qs}` : '');
  return base + hash;
}

/** ?view=agora ou hash #agora no endereço atual. */
function urlRequestsAgoraPreview(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const p = new URLSearchParams(window.location.search);
    return p.get('view') === AGORA_URL_PARAM || window.location.hash === '#agora';
  } catch {
    return false;
  }
}

function stripAgoraFromUrlPreserveParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('view');
  if (url.hash === '#agora') url.hash = '';
  const next = buildPathWithSearch(url.pathname, url.searchParams, url.hash);
  window.history.replaceState(window.history.state, '', next);
}

function pushUrlAgoraView(): void {
  const url = new URL(window.location.href);
  url.searchParams.set('view', AGORA_URL_PARAM);
  if (url.hash === '#agora') url.hash = '';
  const next = buildPathWithSearch(url.pathname, url.searchParams, url.hash);
  window.history.pushState({ [AGORA_HIST_KEY]: AGORA_URL_PARAM }, '', next);
}

/** Carga inicial: ?view=agora ou #agora — sem pedido público (/pedido/…). */
function parseInitialClientViewFromLocation(): ViewType {
  if (typeof window === 'undefined') return 'home';
  try {
    if (/^\/pedido\//.test(window.location.pathname)) return 'home';
    if (urlRequestsAgoraPreview()) return 'agora';
    const params = new URLSearchParams(window.location.search);
    if (params.get('loja')) return 'store';
    if (params.get('fornecedor')) return 'supplier-store';
    if (params.get('v') === 'directory') return 'directory';
  } catch {
    /* ignore */
  }
  return 'home';
}

function parseInitialVendorIdFromLocation(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('loja');
  } catch {
    return null;
  }
}

function parseInitialSupplierIdFromLocation(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('fornecedor');
  } catch {
    return null;
  }
}

/** Link amigável /loja/<slug> — resolve o slug para abrir a vitrine. */
function initialStoreSlug(): string | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/loja\/([^/?#]+)\/?$/);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

const EAMB_CLIENT_NAV = 'eambClientNav' as const;

type EambClientNavState = {
  [EAMB_CLIENT_NAV]: 1;
  view: ViewType;
  vendorId: string | null;
  supplierId: string | null;
};

function buildClientNavUrl(
  view: ViewType,
  vendorId: string | null,
  supplierId: string | null
): string {
  const url = new URL(window.location.href);
  // Link direto /loja/<slug>: o pathname do slug não pode persistir depois de sair
  // da vitrine, senão o cliente fica preso (a home deixaria de ser "/").
  if (/^\/loja\/[^/?#]+/.test(url.pathname)) {
    url.pathname = '/';
  }
  if (view === 'agora') {
    url.searchParams.set('view', AGORA_URL_PARAM);
  } else {
    url.searchParams.delete('view');
    if (url.hash === '#agora') url.hash = '';
  }
  if (view === 'store' && vendorId) url.searchParams.set('loja', vendorId);
  else url.searchParams.delete('loja');
  if (view === 'supplier-store' && supplierId) {
    url.searchParams.set('fornecedor', supplierId);
  } else {
    url.searchParams.delete('fornecedor');
  }
  if (view === 'directory') url.searchParams.set('v', 'directory');
  else url.searchParams.delete('v');
  return buildPathWithSearch(url.pathname, url.searchParams, url.hash);
}

interface RegisterPayload {
  email: string;
  password: string;
  slug?: string;
  role: Role;
  name: string;
  phone?: string;
  storeName?: string;
  regionalId?: string;
  vendorType?: 'LIVRE' | 'REGIONAL';
  campaign?: string | null;
  category?: VendorCategory;
  documentType?: 'PF' | 'PJ';
  cpfCnpj?: string;
  cpf?: string;
  cnpj?: string;
  preferences?: string[];
  address?: {
    cep: string;
    logradouro: string;
    numero: string;
    bairro: string;
    cidade: string;
    uf: string;
    complemento: string;
  };
  // campos de driver
  cnh?: string;
  vehicleType?: string;
  licensePlate?: string;
  serviceArea?: string[];
  isFlexDriver?: boolean;
  // campos de prefeitura
  employeeId?: string;
  department?: string;
  cityHallRole?: string;
  cityHallOrg?: string;
}

// Card de ambulante: visual premium e informativo
const VENDOR_STATUS_CONFIG: Record<string, { text: string; bg: string; dot: string }> = {
  REGULATED:            { text: 'Autorizado', bg: 'bg-emerald-500', dot: 'bg-emerald-400' },
  PENDING_VERIFICATION: { text: 'Em Análise', bg: 'bg-amber-400',   dot: 'bg-amber-400' },
  UNREGULATED:          { text: '',           bg: '',                dot: 'bg-slate-300' },
  SUSPENDED:            { text: 'Suspenso',   bg: 'bg-red-500',     dot: 'bg-red-400' },
  REVOKED:              { text: 'Revogado',   bg: 'bg-gray-500',    dot: 'bg-gray-400' },
};

function VendorCard({ v, onSelect }: { v: Vendor; onSelect: (id: string) => void }) {
  const status = VENDOR_STATUS_CONFIG[v.vendorStatus || 'UNREGULATED'];
  const isOpen = isVendorStoreLive(v);
  return (
    <div
      onClick={() => onSelect(v.id)}
      className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden cursor-pointer group hover:shadow-lg hover:border-emerald-200 hover:-translate-y-0.5 transition-all duration-300 touch-manipulation"
    >
      {/* Imagem 100% limpa - destaque total para foto do vendedor */}
      <div className="relative h-[140px] overflow-hidden bg-slate-100">
        <img
          src={v.banner || v.image || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200'}
          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
          alt={v.storeName}
          loading="lazy"
        />
      </div>

      {/* Informações abaixo da imagem */}
      <div className="px-3 pt-2.5 pb-2.5">
        {/* Nome + Status Aberto/Fechado */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-[14px] font-extrabold text-slate-800 leading-tight line-clamp-1 flex-1">
            {v.storeName}
          </h3>
          <span className={`flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${isOpen ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
            {isOpen ? 'Aberto' : 'Loja fechada'}
          </span>
        </div>

        {/* Localização */}
        <p className="text-[10px] text-slate-500 font-medium flex items-center gap-1 mb-2">
          <span>📍</span>
          <span className="truncate">
            {v.regionalId ? `Regional ${v.regionalId.split(' ')[1]}` : 'Fortaleza, CE'}
          </span>
        </p>

        {/* Faixa de badges */}
        <div className="flex flex-wrap gap-1 mb-2">
          {status.text && (
            <span className={`${status.bg} text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-sm flex items-center gap-1`}>
              <span>✓</span>
              {status.text}
            </span>
          )}
          <span className="bg-amber-100 text-amber-800 text-[9px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
            <span>⚡</span>
            Entrega Rápida
          </span>
        </div>

        {/* Rodapé: categoria + CTA */}
        <div className="flex items-center justify-between pt-1.5 border-t border-slate-100">
          <p className="text-[10px] text-slate-400 truncate font-medium">
            {v.category ? `${v.category.charAt(0).toUpperCase()}${v.category.slice(1)}` : 'Ambulante'}
          </p>
          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full flex-shrink-0">
            Ver cardápio →
          </span>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <AppProvider>
        <MainApp />
      </AppProvider>
    </AppErrorBoundary>
  );
}

function MainApp() {
  const { state, actions, dispatch } = useApp();
  const scrollClasses = useResponsiveScroll();
  const publicOrderId = useMemo(() => {
    const match = window.location.pathname.match(/^\/pedido\/([^/?#]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  }, []);
  
  // drivers agora vem EXCLUSIVAMENTE do AppContext (Firebase = fonte da verdade)
  const drivers = state.drivers;
  // messages agora vem EXCLUSIVAMENTE do AppContext (Firebase = fonte da verdade)
  const messages = state.messages || [];

  const [activeRole, setActiveRole] = useState<Role>('CLIENT');
  const [currentUserStatus, setCurrentUserStatus] = useState<string>('active');
  const [clientView, setClientView] = useState<ViewType>(parseInitialClientViewFromLocation);
  const [isCreditPreviewRoute, setIsCreditPreviewRoute] = useState(() => isCreditPreviewPathname());
  const [isPartnerCreditRoute, setIsPartnerCreditRoute] = useState(() => isPartnerCreditPathname());
  const [activeVendorId, setActiveVendorId] = useState<string | null>(parseInitialVendorIdFromLocation);
  const [storeSlugNotFound, setStoreSlugNotFound] = useState(false);
  useEffect(() => {
    const raw = initialStoreSlug();
    if (!raw) return;
    let cancelled = false;
    void resolveStoreSlug(raw).then((resolved) => {
      if (cancelled) return;
      if (!resolved) {
        setStoreSlugNotFound(true);
        return;
      }
      if (resolved.kind === 'vendor') {
        setActiveVendorId(resolved.uid);
        setClientView('store');
      } else {
        setActiveSupplierId(resolved.uid);
        setClientView('supplier-store');
      }
    }).catch(() => { if (!cancelled) setStoreSlugNotFound(true); });
    return () => { cancelled = true; };
  }, []);
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [resolvedVendorFallback, setResolvedVendorFallback] = useState<Vendor | null>(null);
  const [vendorLinkState, setVendorLinkState] = useState<'loading' | 'linked' | 'missing'>('loading');
  const vendorWarnRef = useRef<string>('');
  const vendorsRef = useRef(state.vendors);
  const ratingModalSessionOpenedRef = useRef(false);

  useEffect(() => {
    vendorsRef.current = state.vendors;
  }, [state.vendors]);

  // Histórico SPA: voltar no Explorer mantém loja/diretório em vez de sair do app
  const skipClientNavPushRef = useRef(false);
  const clientNavSeededRef = useRef(false);
  const lastClientNavUrlRef = useRef<string | null>(null);
  const [cityHallProfile, setCityHallProfile] = useState<import('./types').CityHallAccount | null>(null);
  /** Fase 1 GovTech — painel novo isolado; default false = UX legado intacta. */
  const [showGovTechMunicipal, setShowGovTechMunicipal] = useState(false);
  /** Tenant GovTech (municipalUsers.orgId) — SaaS; não usar cityHallOrg legado. */
  const [municipalTenantOrgId, setMunicipalTenantOrgId] = useState<string | null>(null);
  const [language, setLanguage] = useState<Language>('pt');
  const [cart, setCart] = useState<CartItem[]>(() => loadCartSession()?.items ?? []);
  // FIX: Rastrear vendorId do carrinho para isolar por loja
  const [cartVendorId, setCartVendorId] = useState<string | null>(
    () => loadCartSession()?.vendorId ?? null
  );
  const [customizingProduct, setCustomizingProduct] = useState<Product | null>(null);
  
  // NOVO: Filtro por categoria
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Persistir carrinho (depois dos useState — evita TDZ / ErrorBoundary em produção)
  useEffect(() => {
    saveCartSession(cart, cartVendorId);
  }, [cart, cartVendorId]);

  // publicOrderId: return movido para depois de todos os hooks (regras do React — evita crash iOS/StrictMode)

  // SUPPLIER: buscar ou criar documento direto do Firestore se não estiver em state.suppliers
  useEffect(() => {
    if (activeRole !== 'SUPPLIER' || !authUserId || state.isLoading) return;
    const alreadyLoaded = state.suppliers.some(
      s => s.id === authUserId || s.uid === authUserId
    );
    if (alreadyLoaded) return;

    const fetchOrCreateSupplierDoc = async () => {
      try {
        const supplierSnap = await getDoc(doc(db, 'suppliers', authUserId));
        if (supplierSnap.exists()) {
          // Documento encontrado - adicionar ao estado
          const data = { id: supplierSnap.id, uid: supplierSnap.id, ...supplierSnap.data() } as Supplier;
          actions.addSupplier(data);
        } else {
          // Documento não existe - criar a partir do perfil do usuário
          const userSnap = await getDoc(doc(db, 'users', authUserId));
          const userData = userSnap.exists() ? userSnap.data() : {};
          const currentUser = auth.currentUser;
          const newSupplier: Supplier = {
            id: authUserId,
            uid: authUserId,
            companyName: userData.storeName || userData.name || currentUser?.email || 'Fornecedor',
            tradeName: userData.storeName || userData.name || '',
            email: currentUser?.email || userData.email || '',
            phone: userData.phone || '',
            supplierStatus: (userData.userStatus === 'active' ? 'active' : 'pending') as Supplier['supplierStatus'],
            createdAt: userData.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          await setDoc(doc(db, 'suppliers', authUserId), newSupplier, { merge: true });
          actions.addSupplier(newSupplier);
          console.log('[App] Documento SUPPLIER criado automaticamente:', authUserId);
        }
      } catch (e) {
        console.warn('[App] Não foi possível buscar/criar documento do fornecedor:', e);
      }
    };

    fetchOrCreateSupplierDoc();
  }, [activeRole, authUserId, state.isLoading, state.suppliers]);


  // Carregar perfil CITY_HALL do Firebase
  useEffect(() => {
    if (activeRole !== 'CITY_HALL' || !authUserId) {
      setCityHallProfile(null);
      setMunicipalTenantOrgId(null);
      return;
    }
    const fetchCityHallProfile = async () => {
      const resolveRegionalId = (
        ...candidates: Array<unknown>
      ): import('./types').RegionalId => {
        for (const candidate of candidates) {
          if (typeof candidate !== 'string') continue;
          const canonical = normalizeRegionalId(candidate);
          if (canonical) {
            return canonical as import('./types').RegionalId;
          }
        }
        return 'Regional 1';
      };
      const inferOrgFromRole = (role: unknown): 'AGEFIS' | 'PREFEITURA' | undefined => {
        const r = typeof role === 'string' ? role : '';
        // Mesa / central = Prefeitura; campo = AGEFIS
        if (r === 'fiscal_desk' || r === 'secretary_regional' || r === 'superintendent') return 'PREFEITURA';
        if (r === 'fiscal_field' || r === 'secretary_agefis') return 'AGEFIS';
        return undefined;
      };
      const orgFrom = (a?: string, b?: string, roleHint?: unknown): 'AGEFIS' | 'PREFEITURA' => {
        const x = (a || b || '').toString().trim().toUpperCase();
        if (x === 'PREFEITURA') return 'PREFEITURA';
        if (x === 'AGEFIS') return 'AGEFIS';
        const inferred = inferOrgFromRole(roleHint);
        if (inferred) return inferred;
        return 'AGEFIS';
      };
      try {
        const [snap, userSnap, municipalUserSnap] = await Promise.all([
          getDoc(doc(db, 'cityHallAccounts', authUserId)),
          getDoc(doc(db, 'users', authUserId)),
          getDoc(doc(db, 'municipalUsers', authUserId)),
        ]);
        if (municipalUserSnap.exists()) {
          const mu = municipalUserSnap.data();
          if (mu?.orgId && mu.isActive !== false) {
            setMunicipalTenantOrgId(String(mu.orgId));
            // Piloto: abrir GovTech direto (sem depender de org_AGEFIS)
            setShowGovTechMunicipal(true);
          } else {
            setMunicipalTenantOrgId(null);
          }
        } else {
          setMunicipalTenantOrgId(null);
        }
        const userData = userSnap.exists() ? userSnap.data() : {};
        if (snap.exists()) {
          const data = snap.data();
          const roleHint = data.cityHallRole || userData.cityHallRole;
          setCityHallProfile({
            id: authUserId,
            name: data.name || 'Servidor',
            email: data.email || '',
            employeeId: data.employeeId || '',
            regionalId: resolveRegionalId(
              data.regionalId,
              data.regional,
              userData.regionalId,
              userData.regional
            ),
            department: data.department || '',
            cityHallRole: data.cityHallRole || 'fiscal_field',
            cityHallOrg: orgFrom(data.cityHallOrg, userData.cityHallOrg, roleHint),
            status: data.status || 'active',
            subscriptionStatus: data.subscriptionStatus || 'active',
          });
          console.log('Perfil CITY_HALL carregado do Firebase');
        } else {
          const currentUser = auth.currentUser;
          const roleHint = userData.cityHallRole;
          setCityHallProfile({
            id: authUserId,
            name: userData.name || currentUser?.displayName || 'Servidor',
            email: currentUser?.email || userData.email || '',
            employeeId: userData.employeeId || '',
            regionalId: resolveRegionalId(
              userData.regionalId,
              userData.regional
            ),
            department: userData.department || '',
            cityHallRole: userData.cityHallRole || 'fiscal_field',
            cityHallOrg: orgFrom(undefined, userData.cityHallOrg, roleHint),
            status: 'active',
            subscriptionStatus: 'active',
          });
          console.log('Perfil CITY_HALL criado via fallback');
        }
      } catch (e) {
        console.error('Erro ao carregar perfil CITY_HALL:', e);
        try {
          const userSnap = await getDoc(doc(db, 'users', authUserId));
          const userData = userSnap.exists() ? userSnap.data() : {};
          const cityHallOrg = orgFrom(
            userData.cityHallOrg as string | undefined,
            undefined,
            userData.cityHallRole
          );
          setCityHallProfile({
            id: authUserId,
            name: auth.currentUser?.email || 'Servidor',
            email: auth.currentUser?.email || '',
            employeeId: '',
            regionalId: resolveRegionalId(
              userData.regionalId as string | undefined,
              (userData as { regional?: string }).regional
            ),
            department: '',
            cityHallRole: (userData.cityHallRole as import('./types').CityHallRole) || 'fiscal_field',
            cityHallOrg,
            status: 'active',
            subscriptionStatus: 'active',
          });
        } catch {
          setCityHallProfile({
            id: authUserId,
            name: auth.currentUser?.email || 'Servidor',
            email: auth.currentUser?.email || '',
            employeeId: '',
            regionalId: 'Regional 1',
            department: '',
            cityHallRole: 'fiscal_field',
            cityHallOrg: 'AGEFIS',
            status: 'active',
            subscriptionStatus: 'active',
          });
        }
      }
    };
    fetchCityHallProfile();
  }, [activeRole, authUserId]);

  // Estados do Login Controlados
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState<{ show: boolean; order: Order | null; vendorPhone?: string; vendorName?: string }>({ show: false, order: null });

  const [ratingModal, setRatingModal] = useState<{ show: boolean; order: Order | null }>({ show: false, order: null });
  const [ratedOrders, setRatedOrders] = useState<string[]>([]);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Fecha overlays do fluxo CLIENT ao entrar em painel profissional (evita backdrop órfão).
  useEffect(() => {
    if (!isDashboardRole(activeRole)) return;

    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(false);
    setIsCartOpen(false);
    setIsLoggingIn(false);
    setOrderSuccess({ show: false, order: null });
    setRatingModal({ show: false, order: null });
    removeOrphanOverlays();
    releaseBodyScrollLock();
  }, [activeRole]);

  // RESTAURAR SESSÃO DO FIREBASE AUTH
  useEffect(() => {
    log.info('Verificando sessão Firebase...');
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        log.info('Usuário autenticado:', user.uid);
        
        try {
          // Emails protegidos entram como OWNER imediatamente (evita esperar roundtrip do Firestore).
          const PROTECTED_OWNER_EMAILS = ['gestor@eambulante.com'];
          const isProtectedOwner = PROTECTED_OWNER_EMAILS.includes((user.email || '').toLowerCase());
          if (isProtectedOwner) {
            setAuthUserId(user.uid);
            setActiveRole('OWNER');
            setCurrentUserStatus('active');
            setIsCheckingAuth(false);

            // Sincroniza perfil em segundo plano sem bloquear navegação.
            void (async () => {
              try {
                const userRef = doc(db, 'users', user.uid);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                  const profile = userSnap.data();
                  if (profile.role !== 'OWNER' || profile.userStatus !== 'active') {
                    await setDoc(userRef, { role: 'OWNER', userStatus: 'active' }, { merge: true });
                    log.info('Perfil OWNER protegido sincronizado');
                  }
                } else {
                  await setDoc(userRef, {
                    uid: user.uid,
                    email: user.email,
                    role: 'OWNER',
                    name: 'Gestor E-Ambulantes',
                    userStatus: 'active',
                    createdAt: new Date().toISOString(),
                  }, { merge: true });
                  log.info('Perfil OWNER protegido criado');
                }
              } catch (ownerSyncErr) {
                log.warn('Falha ao sincronizar perfil OWNER protegido:', ownerSyncErr);
              }
            })();
            return;
          }

          // Buscar perfil do usuário no Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));

          if (userDoc.exists()) {
            const profile = userDoc.data();

            // Se o email é protegido mas o role está errado, corrige automaticamente
            let resolvedRole: Role = isProtectedOwner ? 'OWNER' : (profile.role as Role);
            if (!isProtectedOwner) {
              resolvedRole = await resolveEffectiveUserRole(user.uid, resolvedRole);
              if (resolvedRole === 'VENDOR' && profile.role !== 'VENDOR') {
                void syncUserRoleIfVendor(user.uid, resolvedRole, profile.role);
                log.info('[Auth] Role inferido VENDOR (loja no Firestore, perfil era', profile.role, ')');
              }
            }
            if (isProtectedOwner && profile.role !== 'OWNER') {
              try {
                await setDoc(doc(db, 'users', user.uid), { role: 'OWNER', userStatus: 'active' }, { merge: true });
                log.info('Role corrigido automaticamente para OWNER');
              } catch { /* silencioso */ }
            }

            log.info('Perfil encontrado:', resolvedRole);

            // Roles que exigem aprovação do Owner nunca devem ter status padrão 'active'
            const rolesRequiringApproval: Role[] = ['VENDOR', 'DRIVER', 'SUPPLIER', 'CITY_HALL'];
            const safeFallbackStatus = rolesRequiringApproval.includes(resolvedRole) ? 'pending_approval' : 'active';

            setAuthUserId(user.uid);
            setActiveRole(resolvedRole);
            setCurrentUserStatus(profile.userStatus || safeFallbackStatus);
            // Libera UI imediatamente; sincronizações específicas rodam em background.
            setIsCheckingAuth(false);
            
            // VENDOR: vincular loja real no Firestore (nao inventar vendor local)
            if (resolvedRole === 'VENDOR') {
              void (async () => {
                const resolved = await resolveVendorByAuthUid(user.uid);
                if (resolved) {
                  setResolvedVendorFallback(resolved);
                  setVendorLinkState('linked');
                  if (!vendorsRef.current.some((v) => v.id === resolved.id)) {
                    log.info('[VENDOR] Loja vinculada no login:', resolved.id);
                    actions.addVendor(resolved, { persist: false });
                  }
                } else {
                  setResolvedVendorFallback(null);
                  setVendorLinkState('missing');
                  log.warn('[VENDOR] Loja nao encontrada no login. UID:', user.uid);
                }
              })();
            }
          } else if (isProtectedOwner) {
            // Perfil não existe ainda - cria automaticamente como OWNER
            try {
              await setDoc(doc(db, 'users', user.uid), {
                uid: user.uid,
                email: user.email,
                role: 'OWNER',
                name: 'Gestor E-Ambulantes',
                userStatus: 'active',
                createdAt: new Date().toISOString(),
              });
              log.info('Perfil OWNER criado automaticamente');
              setAuthUserId(user.uid);
              setActiveRole('OWNER');
              setCurrentUserStatus('active');
              setIsCheckingAuth(false);
            } catch (e) {
              log.error('? Erro ao criar perfil OWNER:', e);
              setAuthUserId(user.uid);
              setActiveRole('OWNER');
              setCurrentUserStatus('active');
              setIsCheckingAuth(false);
            }
          } else {
            // Perfil ainda não gravado (race condition: onAuthStateChanged dispara antes do setDoc)
            // Aguardar 1.5s e tentar novamente uma vez
            log.warn('Perfil não encontrado, aguardando gravação...');
            await new Promise(r => setTimeout(r, 1500));
            try {
              const retryDoc = await getDoc(doc(db, 'users', user.uid));
              if (retryDoc.exists()) {
                const profile = retryDoc.data();
                let resolvedRole: Role = (profile.role as Role) || 'CLIENT';
                resolvedRole = await resolveEffectiveUserRole(user.uid, resolvedRole);
                if (resolvedRole === 'VENDOR' && profile.role !== 'VENDOR') {
                  void syncUserRoleIfVendor(user.uid, resolvedRole, profile.role);
                }
                const rolesRequiringApproval: Role[] = ['VENDOR', 'DRIVER', 'SUPPLIER', 'CITY_HALL'];
                const safeFallbackStatus = rolesRequiringApproval.includes(resolvedRole) ? 'pending_approval' : 'active';
                setAuthUserId(user.uid);
                setActiveRole(resolvedRole);
                setCurrentUserStatus(profile.userStatus || safeFallbackStatus);
                setIsCheckingAuth(false);
                log.info('? Perfil carregado na retentativa:', resolvedRole, profile.userStatus);
                if (resolvedRole === 'VENDOR') {
                  void (async () => {
                    const resolved = await resolveVendorByAuthUid(user.uid);
                    if (resolved) {
                      setResolvedVendorFallback(resolved);
                      setVendorLinkState('linked');
                      if (!vendorsRef.current.some((v) => v.id === resolved.id)) {
                        actions.addVendor(resolved, { persist: false });
                      }
                    } else {
                      setResolvedVendorFallback(null);
                      setVendorLinkState('missing');
                    }
                  })();
                }
              } else {
                const inferredRole = await resolveEffectiveUserRole(user.uid, 'CLIENT');
                if (inferredRole === 'VENDOR') {
                  void syncUserRoleIfVendor(user.uid, inferredRole, 'CLIENT');
                  setAuthUserId(user.uid);
                  setActiveRole('VENDOR');
                  setCurrentUserStatus('active');
                  setIsCheckingAuth(false);
                  void (async () => {
                    const resolved = await resolveVendorByAuthUid(user.uid);
                    if (resolved) {
                      setResolvedVendorFallback(resolved);
                      setVendorLinkState('linked');
                      if (!vendorsRef.current.some((v) => v.id === resolved.id)) {
                        actions.addVendor(resolved, { persist: false });
                      }
                    } else {
                      setVendorLinkState('missing');
                    }
                  })();
                  log.info('[Auth] VENDOR inferido sem users/{uid}');
                } else {
                  log.warn('Perfil ainda não disponível após retry — usuário como CLIENT');
                  setAuthUserId(user.uid);
                  setActiveRole('CLIENT');
                  setCurrentUserStatus('active');
                  setIsCheckingAuth(false);
                }
              }
            } catch (retryErr) {
              log.error('? Erro na retentativa de perfil:', retryErr);
              setAuthUserId(user.uid);
              setActiveRole('CLIENT');
              setCurrentUserStatus('active');
              setIsCheckingAuth(false);
            }
          }
        } catch (error) {
          log.error('Erro ao verificar autenticação/perfil do usuário:', error);
          setAuthUserId((prev) => prev ?? user.uid);
          setActiveRole((prev) => prev ?? 'CLIENT');
          setCurrentUserStatus((prev) => prev ?? 'active');
          setIsCheckingAuth(false);
        }
      } else {
        log.info('Nenhum usuário autenticado');
        setAuthUserId(null);
        setActiveRole('CLIENT');
        setCurrentUserStatus('active');
        setIsCheckingAuth(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authUserId) return;

    let active = true;

    const fetchProfile = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', authUserId));
        if (!active || !snap.exists()) return;
        const data = snap.data();
        const newStatus: string = data.userStatus || 'active';

        if (newStatus === 'pending_approval' && data.role === 'CITY_HALL') {
          try {
            const chSnap = await getDoc(doc(db, 'cityHallAccounts', authUserId));
            if (chSnap.exists() && chSnap.data().status === 'active') {
              log.info('[AutoFix] cityHallAccounts ativo mas users pendente — corrigindo...');
              await setDoc(doc(db, 'users', authUserId), { userStatus: 'active' }, { merge: true });
              if (active) setCurrentUserStatus('active');
              return;
            }
          } catch (e) {
            log.warn('[AutoFix] Erro ao verificar cityHallAccounts:', e);
          }
        }

        // Owner aprovou vendors/{uid} (REGULATED) mas users ficou pending — regra antiga bloqueava.
        if (newStatus === 'pending_approval' && data.role === 'VENDOR') {
          try {
            const vSnap = await getDoc(doc(db, 'vendors', authUserId));
            const vStatus = vSnap.exists() ? String(vSnap.data()?.vendorStatus || '') : '';
            if (vStatus === 'REGULATED') {
              log.info('[AutoFix] vendor REGULATED mas users pendente — liberando acesso...');
              await setDoc(
                doc(db, 'users', authUserId),
                { userStatus: 'active', vendorStatus: 'REGULATED' },
                { merge: true }
              );
              if (active) setCurrentUserStatus('active');
              return;
            }
          } catch (e) {
            log.warn('[AutoFix] Erro ao verificar vendorStatus:', e);
          }
        }

        if (active) {
          setCurrentUserStatus((prev) => {
            if (prev !== newStatus) {
              log.info('Status atualizado:', prev, '->', newStatus);
            }
            return newStatus;
          });
        }
      } catch (e) {
        log.warn('Erro ao buscar perfil:', e);
      }
    };

    fetchProfile();
    const interval = setInterval(fetchProfile, 5 * 60 * 1000);
    const onFocus = () => fetchProfile();
    window.addEventListener('focus', onFocus);

    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [authUserId]);

  // Monitora pedidos entregues e abre avaliação automaticamente
  // Só abre se:
  // 1. Usuário estiver LOGADO
  // 2. Auth já completou verificação
  // 3. Pedido foi entregue nas últimas 24h
  // 4. Pedido pertence ao usuário logado
  useEffect(() => {
    // NÃO mostrar modal se:
    // - Não estiver logado
    // - Auth ainda estiver verificando
    // - Não for role CLIENT
    if (!authUserId || isCheckingAuth || activeRole !== 'CLIENT') {
      return;
    }

    // Debounce para evitar múltiplos disparos
    const timeoutId = setTimeout(() => {
      const now = Date.now();
      const oneDayAgo = now - (24 * 60 * 60 * 1000); // 24 horas atrás
      
      const deliveredOrders = state.orders.filter(o => {
        // Verificar status
        const isDelivered = o.status === 'delivered' || o.status === 'completed';
        // Verificar se é do usuário logado
        const isUserOrder = o.clientUid === authUserId;
        // Verificar se não foi avaliado
        const notRated = !ratedOrders.includes(o.id);
        // Verificar se é recente (últimas 24h)
        const isRecent = o.timestamp ? Number(o.timestamp) > oneDayAgo : false;
        
        return isDelivered && isUserOrder && notRated && isRecent;
      });

      log.info('[RatingModal] Verificando pedidos para avaliação:', {
        totalOrders: state.orders.length,
        deliveredCount: deliveredOrders.length,
      });

      if (deliveredOrders.length > 0 && !ratingModalSessionOpenedRef.current) {
        const latestDelivered = deliveredOrders[0];
        ratingModalSessionOpenedRef.current = true;
        log.info('? [RatingModal] Abrindo avaliação para pedido:', latestDelivered.id);
        setRatingModal({ show: true, order: latestDelivered });
        setRatedOrders(prev => [...prev, latestDelivered.id]);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [state.orders, activeRole, ratedOrders, authUserId, isCheckingAuth]);


  const vendors = state.vendors;
  const suppliers = state.suppliers;
    const [activeSupplierId, setActiveSupplierId] = useState<string | null>(parseInitialSupplierIdFromLocation);
  const products = state.products;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (publicOrderId) return;
    if (isCreditPreviewPathname() || isPartnerCreditPathname()) return;

    const navState: EambClientNavState = {
      [EAMB_CLIENT_NAV]: 1,
      view: clientView,
      vendorId: activeVendorId,
      supplierId: activeSupplierId,
    };
    const nextUrl = buildClientNavUrl(clientView, activeVendorId, activeSupplierId);

    if (!clientNavSeededRef.current) {
      clientNavSeededRef.current = true;
      lastClientNavUrlRef.current = nextUrl;
      try {
        window.history.replaceState(
          { ...(window.history.state || {}), ...navState },
          '',
          nextUrl
        );
      } catch {
        /* ignore */
      }
      return;
    }

    if (skipClientNavPushRef.current) {
      skipClientNavPushRef.current = false;
      lastClientNavUrlRef.current = nextUrl;
      return;
    }

    // Evita poluir o histórico com estados idênticos (re-render / Strict Mode)
    if (lastClientNavUrlRef.current === nextUrl) return;
    lastClientNavUrlRef.current = nextUrl;

    try {
      window.history.pushState(navState, '', nextUrl);
    } catch {
      /* ignore */
    }
  }, [clientView, activeVendorId, activeSupplierId, publicOrderId]);

  // iOS Safari bfcache: ao voltar pela pilha do navegador, realinha view com a URL
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      if (publicOrderId) return;
      if (isCreditPreviewPathname() || isPartnerCreditPathname()) return;
      try {
        const view = parseInitialClientViewFromLocation();
        const vendorId = parseInitialVendorIdFromLocation();
        const supplierId = parseInitialSupplierIdFromLocation();
        skipClientNavPushRef.current = true;
        setClientView(view);
        setActiveVendorId(vendorId);
        setActiveSupplierId(supplierId);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [publicOrderId]);
  
  const VENDOR_CATEGORIES: Array<{ id: string; label: string; icon: React.ReactNode }> = [
    { id: 'comida',     label: 'Comida',      icon: <UtensilsCrossed size={16} strokeWidth={2} /> },
    { id: 'moda',       label: 'Moda',        icon: <Shirt size={16} strokeWidth={2} /> },
    { id: 'artesanato', label: 'Artesanato',  icon: <Palette size={16} strokeWidth={2} /> },
    { id: 'presentes',  label: 'Presentes',   icon: <Gift size={16} strokeWidth={2} /> },
    { id: 'mobilidade', label: 'Mobilidade',  icon: <Bike size={16} strokeWidth={2} /> },
    { id: 'fitness',    label: 'Fitness',     icon: <Dumbbell size={16} strokeWidth={2} /> },
    { id: 'servicos',   label: 'Serviços',    icon: <Wrench size={16} strokeWidth={2} /> },
    { id: 'outros',     label: 'Outros',      icon: <Package size={16} strokeWidth={2} /> },
  ];

  // FILTRO DE VENDORS — visibilidade pública (presença NÃO filtra; só ordena abertos primeiro)
  // Vitrine: fornecedor inativo ou suspenso não aparece (a página direta ?fornecedor= continua).
  const publicSuppliers = useMemo(
    () => suppliers.filter((s) => s.supplierStatus !== 'inactive' && s.supplierStatus !== 'suspended'),
    [suppliers]
  );

  const publicVendors = useMemo(() => {
    const supplierIds = new Set(suppliers.map((s) => s.id));
    return sortVendorsOpenFirst(vendors.filter((v) => isPublicListingVendor(v, supplierIds)));
  }, [vendors, suppliers]);

  const filteredVendors = useMemo(() => {
    if (!selectedCategory) return publicVendors;
    return publicVendors.filter((v) => v.category === selectedCategory);
  }, [publicVendors, selectedCategory]);

  const vendorsByCategory = useMemo(() => {
    const groups: Record<string, typeof vendors> = {};
    publicVendors.forEach((v) => {
      const cat = v.category || 'outros';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(v);
    });
    return groups;
  }, [publicVendors]);

  const openVendorCount = useMemo(
    () => publicVendors.filter((v) => isVendorStoreLive(v)).length,
    [publicVendors]
  );

  // supplier-store: redirect só após carga — evita setState durante render (iOS/React estrito)
  useEffect(() => {
    if (state.isLoading) return;
    if (clientView !== 'supplier-store' || !activeSupplierId) return;
    const roleOk = activeRole === 'VENDOR' || activeRole === 'CLIENT' || !activeRole;
    if (!roleOk) return;
    const found = suppliers.find((s) => s.id === activeSupplierId);
    if (!found) {
      setClientView(activeRole === 'VENDOR' ? 'suppliers' : 'directory');
    }
  }, [state.isLoading, clientView, activeSupplierId, activeRole, suppliers]);
  
  // Função para obter nome da categoria
  const getCategoryName = (categoryId: string): string => {
    const categoryNames: Record<string, string> = {
      'comida': 'Comida',
      'moda': 'Moda',
      'artesanato': 'Artesanato',
      'presentes': 'Presentes & Cestas',
      'mobilidade': 'Mobilidade',
      'fitness': 'Fitness & Bem-estar',
      'servicos': 'Serviços',
      'outros': 'Outros',
      'atacado': 'Atacado & Fornecedores',
    };
    return categoryNames[categoryId] || categoryId;
  };

  const currentVendor = useMemo(() => {
    if (!authUserId) return null;
    const fromState = vendors.find((v) => vendorMatchesAuthUid(v, authUserId));
    return fromState ?? resolvedVendorFallback;
  }, [vendors, authUserId, resolvedVendorFallback]);

  // F2 — queries scoped por vendorId (sem limit global 600)
  const supplierDashId =
    activeRole === 'SUPPLIER' && authUserId ? authUserId : null;
  const scopedSupplierDash = useScopedProducts(supplierDashId, { mode: 'live', pageSize: 50 });

  // F2-ADAPTER-VENDOR-DASH: sobrevive sem listener global até F2.1 (não altera VendorDashboard.tsx)
  const vendorDashId =
    activeRole === 'VENDOR' && currentVendor ? currentVendor.id : null;
  const scopedVendorDash = useScopedProducts(vendorDashId, { mode: 'live', pageSize: 50 });

  // F3 + 5c — vitrine leve: seções = categorias dos produtos (não só departments manuais)
  const vendorStoreScopeId =
    clientView === 'store' && activeVendorId ? activeVendorId : null;
  const vendorCategoryDiscovery = useVendorCategorySections(
    vendorStoreScopeId,
    vendors.find((x) => x.id === vendorStoreScopeId)?.ordemDepartamentos
  );
  const vendorStoreSections = useMemo(() => {
    if (!vendorStoreScopeId) return [] as string[];
    const v = vendors.find((x) => x.id === vendorStoreScopeId);
    const fromDoc = v
      ? resolveStoreSections({
          customCategories: v.customCategories,
          ordemDepartamentos: v.ordemDepartamentos,
        })
      : [];
    // Com varredura pronta: só categorias que existem nos produtos (sem dept vazio)
    if (vendorCategoryDiscovery.ready && vendorCategoryDiscovery.sections.length > 0) {
      return vendorCategoryDiscovery.sections;
    }
    return mergeSectionLists(vendorCategoryDiscovery.sections, fromDoc);
  }, [
    vendorStoreScopeId,
    vendors,
    vendorCategoryDiscovery.sections,
    vendorCategoryDiscovery.ready,
  ]);
  const vendorStoreCatalog = useStoreSectionCatalog(vendorStoreScopeId, vendorStoreSections);

  const supplierStoreScopeId =
    clientView === 'supplier-store' && activeSupplierId ? activeSupplierId : null;
  const supplierCategoryDiscovery = useVendorCategorySections(
    supplierStoreScopeId,
    suppliers.find((x) => x.id === supplierStoreScopeId)?.ordemDepartamentos
  );
  const supplierStoreSections = useMemo(() => {
    if (!supplierStoreScopeId) return [] as string[];
    const s = suppliers.find((x) => x.id === supplierStoreScopeId);
    const fromDoc = s
      ? resolveStoreSections({
          departments: s.departments,
          tags: s.tags,
          ordemDepartamentos: s.ordemDepartamentos,
        })
      : [];
    if (supplierCategoryDiscovery.ready && supplierCategoryDiscovery.sections.length > 0) {
      return supplierCategoryDiscovery.sections;
    }
    return mergeSectionLists(supplierCategoryDiscovery.sections, fromDoc);
  }, [
    supplierStoreScopeId,
    suppliers,
    supplierCategoryDiscovery.sections,
    supplierCategoryDiscovery.ready,
  ]);
  const supplierStoreCatalog = useStoreSectionCatalog(
    supplierStoreScopeId,
    supplierStoreSections
  );

  // F2-ADAPTER: cache escopo-ativo em state.products (Opção A)
  useEffect(() => {
    if (supplierDashId) {
      dispatch({ type: 'SET_DATA', payload: { products: scopedSupplierDash.products } });
      return;
    }
    if (vendorDashId) {
      dispatch({ type: 'SET_DATA', payload: { products: scopedVendorDash.products } });
      return;
    }
    if (vendorStoreScopeId) {
      dispatch({ type: 'SET_DATA', payload: { products: vendorStoreCatalog.products } });
      return;
    }
    if (supplierStoreScopeId) {
      dispatch({ type: 'SET_DATA', payload: { products: supplierStoreCatalog.products } });
      return;
    }
    dispatch({ type: 'SET_DATA', payload: { products: [] } });
  }, [
    dispatch,
    supplierDashId,
    vendorDashId,
    vendorStoreScopeId,
    supplierStoreScopeId,
    scopedSupplierDash.products,
    scopedVendorDash.products,
    vendorStoreCatalog.products,
    supplierStoreCatalog.products,
  ]);

  const vendorHydrateKeyRef = useRef<string>('');

  useEffect(() => {
    if (activeRole !== 'VENDOR' || !authUserId) {
      vendorHydrateKeyRef.current = '';
      setResolvedVendorFallback(null);
      setVendorLinkState('loading');
      return;
    }

    const matched = state.vendors.find((v) => vendorMatchesAuthUid(v, authUserId));
    if (matched) {
      // Mantém fallback espelhado — listener global com limit() pode dropar o doc depois
      setResolvedVendorFallback((prev) => (prev?.id === matched.id ? prev : matched));
      setVendorLinkState('linked');
      vendorHydrateKeyRef.current = `${authUserId}:linked:${matched.id}`;
      return;
    }

    // Se já temos fallback do resolve, não fica preso em loading só porque o snapshot omitiu a loja
    if (resolvedVendorFallback && vendorMatchesAuthUid(resolvedVendorFallback, authUserId)) {
      setVendorLinkState('linked');
      if (!state.vendors.some((v) => v.id === resolvedVendorFallback.id)) {
        dispatch({ type: 'ADD_VENDOR', payload: resolvedVendorFallback });
      }
      return;
    }

    const attemptKey = `${authUserId}:resolve`;
    if (vendorHydrateKeyRef.current === attemptKey) {
      return;
    }
    vendorHydrateKeyRef.current = attemptKey;
    setVendorLinkState('loading');

    let cancelled = false;

    const run = async () => {
      try {
        const resolved = await resolveVendorByAuthUid(authUserId);
        if (cancelled) return;
        if (resolved) {
          setResolvedVendorFallback(resolved);
          dispatch({ type: 'ADD_VENDOR', payload: resolved });
          setVendorLinkState('linked');
          vendorHydrateKeyRef.current = `${authUserId}:linked:${resolved.id}`;
        } else {
          setResolvedVendorFallback(null);
          setVendorLinkState('missing');
          vendorHydrateKeyRef.current = `${authUserId}:missing`;
        }
      } catch (error) {
        if (!cancelled) {
          log.warn('[VENDOR] fallback lookup falhou:', error);
          setVendorLinkState('missing');
          vendorHydrateKeyRef.current = `${authUserId}:missing`;
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [activeRole, authUserId, state.vendors, resolvedVendorFallback, dispatch]);

  const handleAddToCartClick = (product: Product) => {
    const supplier = suppliers.find((s) => s.id === product.vendorId);
    if (supplier) {
      if (!logStorePresenceCheck('add-to-cart-supplier', supplier)) {
        toast.error('Fornecedor fechado', SUPPLIER_STORE_CLOSED_MESSAGE);
        return;
      }
      setCustomizingProduct(product);
      return;
    }

    const v = vendors.find((x) => x.id === product.vendorId);
    if (!isVendorStoreLive(v)) {
      toast.error('Loja fechada', VENDOR_STORE_CLOSED_MESSAGE);
      return;
    }
    setCustomizingProduct(product);
  };

  // ============================================
  // HANDLER DE REGISTRO (única definição, usada em múltiplos lugares)
  // ============================================
  const handleRegister = useCallback(async (payload: RegisterPayload) => {
    log.info('Iniciando cadastro Firebase');
    
    const normalizedEmail = payload.email.toLowerCase().trim();

    const passwordCheck = validateStrongPassword(payload.password);
    if (!passwordCheck.valid) {
      throw new Error(`Senha fraca: ${passwordCheck.errors.join('; ')}.`);
    }
    
    try {
      const result = await authService.register(
        normalizedEmail,
        payload.password,
        {
          role: payload.role,
          name: payload.name,
          // userStatus é determinado pelo authService com base na role:
          // VENDOR/DRIVER/SUPPLIER/CITY_HALL → 'pending_approval', CLIENT → 'active'
          userStatus: payload.role === 'CLIENT' ? 'active' : 'pending_approval',
          phone: payload.phone,
          regionalId: payload.regionalId as import('./types').RegionalId | undefined,
          // Todos os VENDORs iniciam como PENDING_VERIFICATION - owner aprova depois
        vendorStatus: payload.role === 'VENDOR' ? 'PENDING_VERIFICATION' : undefined,
          storeName: payload.storeName,
          preferences: payload.preferences,
          category: payload.category,
          documentType: payload.documentType,
          cpf: payload.cpf,
          cnpj: payload.cnpj,
          cpfCnpj: payload.cpfCnpj,
          vendorType: payload.vendorType,
          campaign: payload.campaign,
          ...(payload.address ? { address: payload.address } : {}),
          ...(payload.role === 'CITY_HALL'
            ? {
                cityHallRole: (payload.cityHallRole || 'fiscal_field') as import('./types').CityHallRole,
                cityHallOrg:
                  payload.cityHallOrg === 'PREFEITURA' ? ('PREFEITURA' as const) : ('AGEFIS' as const),
                employeeId: payload.employeeId,
                department: payload.department,
              }
            : {}),
          ...(payload.role === 'DRIVER'
            ? {
                vehicleType: payload.vehicleType,
                cnh: payload.cnh,
                licensePlate: payload.licensePlate,
                serviceArea: payload.serviceArea,
                isFlexDriver: payload.isFlexDriver,
              }
            : {}),
        } as any // driver/cityHall extension fields
      );

      log.info('Resultado do registro:', result.success ? 'sucesso' : 'falha');

      if (result.success && result.userId) {
        log.info('? Cadastro realizado com sucesso!');

        // LINK: reservar o slug da loja antes de hidratar o state.
        let storeSlug: string | undefined;
        if (payload.role === 'VENDOR' || payload.role === 'SUPPLIER') {
          try {
            storeSlug = await claimSlug(
              result.userId,
              payload.storeName || payload.name,
              payload.address?.cidade,
              payload.slug
            );
            // Persiste o slug com await (authService já criou o documento da loja).
            const col = payload.role === 'VENDOR' ? 'vendors' : 'suppliers';
            await setDoc(doc(db, col, result.userId), { slug: storeSlug }, { merge: true });
            log.info('Slug reservado:', storeSlug);
          } catch (e) {
            if (storeSlug) await releaseSlug(result.userId, storeSlug).catch(() => {});
            const msg = e instanceof Error ? e.message : 'Não foi possível reservar o link da loja.';
            toast.error('Link indisponível', msg);
            throw new Error(msg);
          }
        }

        // VENDOR: authService.register já grava vendors/{uid} (fonte única).
        // Aqui só hidrata o state local — sem segundo setDoc divergente.
        if (payload.role === 'VENDOR') {
          const newVendor = {
            id: result.userId,
            name: payload.storeName || payload.name,
            ownerName: payload.name,
            storeName: payload.storeName || payload.name,
            slug: storeSlug,
            email: normalizedEmail,
            phone: payload.phone || '',
            image: 'https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=400',
            location: null,
            locationStatus: 'PENDING_ADDRESS' as const,
            ...(payload.address ? { address: payload.address } : {}),
            vendorStatus: 'PENDING_VERIFICATION' as const,
            subscriptionStatus: 'inactive' as const,
            regionalId: payload.regionalId as import('./types').RegionalId | undefined,
            documentType: payload.documentType,
            cpf: payload.documentType === 'PF' ? payload.cpf : undefined,
            cnpj: payload.documentType === 'PJ' ? payload.cnpj : undefined,
            cpfCnpj: payload.cpfCnpj || payload.cpf || payload.cnpj,
            rating: 0,
            category: payload.category,
            vendorType: payload.vendorType,
            campaign: payload.campaign || null,
            payment: { pix: null },
          } as unknown as Vendor;

          log.info('Hidratando vendor no state (Firestore já gravado pelo authService)');
          actions.addVendor(newVendor, { persist: false });
        }
        
        // Se for DRIVER, criar documento na coleção drivers
        if (payload.role === 'DRIVER') {
          const newDriver: Driver = {
            id: result.userId,
            authUserId: result.userId,
            name: payload.name,
            email: normalizedEmail,
            phone: payload.phone || '',
            cpf: payload.cpf || '',
            cnh: payload.cnh || '',
            vehicleType: payload.vehicleType === 'moto' ? 'motorcycle' : payload.vehicleType === 'bike' ? 'bicycle' : payload.vehicleType === 'car' ? 'car' : 'motorcycle',
            vehicle: payload.vehicleType || 'moto',
            plate: payload.licensePlate || '',
            serviceArea: [],
            isOnline: false,
            status: 'offline',
            userStatus: 'pending_approval',
            rating: 5.0,
            currentLocation: { lat: -3.7319, lng: -38.5267 },
            createdAt: new Date().toISOString(),
          };
          
          log.info('Criando driver');
          actions.addDriver(newDriver);
          
          try {
            await setDoc(doc(db, 'drivers', result.userId), removeUndefined(newDriver), { merge: true });
            log.info('? Driver salvo no Firebase');
          } catch (fbError) {
            log.warn('Erro ao salvar driver no Firebase:', fbError);
          }
        }

        // Se for CITY_HALL, criar documento na colecao cityHallAccounts
        if (payload.role === 'CITY_HALL') {
          const cityHallDoc = {
            id: result.userId,
            name: payload.name,
            email: normalizedEmail,
            phone: payload.phone || '',
            employeeId: payload.employeeId || '',
            regionalId: (
              (payload.regionalId as import('./types').RegionalId | undefined) ||
              (payload as { regional?: import('./types').RegionalId }).regional ||
              'Regional 1'
            ),
            additionalRegionals: [] as import('./types').RegionalId[],
            department: payload.department || '',
            cityHallRole: (payload.cityHallRole || 'fiscal_field') as import('./types').CityHallRole,
            cityHallOrg: payload.cityHallOrg || 'AGEFIS',
            status: 'pending_approval' as const,
            subscriptionStatus: 'active' as const,
            createdAt: new Date().toISOString(),
          };

          log.info('Criando perfil CITY_HALL:', cityHallDoc.cityHallOrg, cityHallDoc.cityHallRole);

          try {
            await setDoc(doc(db, 'cityHallAccounts', result.userId), cityHallDoc, { merge: true });
            // Tambem salvar cityHallRole no perfil users/
            await setDoc(doc(db, 'users', result.userId), {
              cityHallRole: cityHallDoc.cityHallRole,
              cityHallOrg: cityHallDoc.cityHallOrg,
              employeeId: cityHallDoc.employeeId,
              department: cityHallDoc.department,
              regionalId: cityHallDoc.regionalId,
            }, { merge: true });
            log.info('? Perfil CITY_HALL salvo no Firebase');
          } catch (fbError) {
            log.warn('Erro ao salvar CITY_HALL no Firebase:', fbError);
          }
        }
        
        // Se for CLIENT, criar documento na coleção clients
        if (payload.role === 'CLIENT') {
          const newClient: Client = {
            id: result.userId,
            name: payload.name,
            email: normalizedEmail,
            phone: payload.phone || '',
            createdAt: new Date().toISOString(),
            addresses: [],
            totalOrders: 0,
            status: 'active',
          };
          
          log.info('Criando client');
          actions.addClient(newClient);
          
          try {
            await setDoc(doc(db, 'clients', result.userId), removeUndefined(newClient), { merge: true });
            log.info('? Client salvo no Firebase');
          } catch (fbError) {
            log.warn('Erro ao salvar client no Firebase:', fbError);
          }
        }
        
        setIsRegisterModalOpen(false);
        setLoginEmail(normalizedEmail);
        setLoginPassword(payload.password);
        
        // Mensagem de sucesso personalizada por tipo
        const roleMessages: Record<string, string> = {
          'CLIENT': 'Pronto! Você já pode fazer pedidos.',
          'VENDOR': 'Seu cadastro está em análise. O administrador irá liberar seu acesso em breve.',
          'DRIVER': 'Seu cadastro está em análise. Aguarde a aprovação do administrador.',
          'SUPPLIER': 'Seu cadastro está em análise. O administrador irá liberar seu acesso em breve.',
          'CITY_HALL': 'Sua credencial está em análise. Aguarde a validação do administrador.',
        };
        
        setTimeout(() => {
          toast.success('Cadastro realizado!', `${roleMessages[payload.role] || ''} Use ${normalizedEmail} para entrar.`);
          setIsLoginModalOpen(true);
        }, 200);
      } else {
        console.error('Erro no cadastro:', result.message, 'code:', (result as { errorCode?: string }).errorCode);
        const err = new Error(result.message || 'Erro ao realizar cadastro') as Error & { code?: string };
        err.code = (result as { errorCode?: string }).errorCode;
        throw err;
      }
    } catch (error: unknown) {
      const e = error as { code?: string; message?: string };
      console.error('Exceção no cadastro (propagando):', e?.code, e?.message, error);
      throw error;
    }
  }, [actions, setIsRegisterModalOpen, setLoginEmail, setLoginPassword, setIsLoginModalOpen]);

  /**
   * FIX: Selecionar loja com reset automático do carrinho
   * Se o usuário tiver itens de outra loja, pergunta antes de limpar
   */
  const handleSelectVendor = (vendorId: string) => {
    // Verificar se há itens de outra loja no carrinho
    if (cart.length > 0 && cartVendorId && cartVendorId !== vendorId) {
      const currentVendorName = vendors.find(v => v.id === cartVendorId)?.storeName || 'loja anterior';
      const newVendorName = vendors.find(v => v.id === vendorId)?.storeName || 'nova loja';
      
      // Limpar carrinho ao trocar de loja (sem confirm bloqueante)
      setCart([]);
      setCartVendorId(null);
      toast.info(`Carrinho atualizado`, `Itens de "${currentVendorName}" removidos.`);
      log.info('Carrinho limpo ao trocar de loja');
    }
    
    setActiveVendorId(vendorId);
    setClientView('store');
  };

  const handleConfirmCustomization = (product: Product, customization: ProductCustomization, quantity: number) => {
    const vendor = vendors.find(v => v.id === product.vendorId);
    const supplier = !vendor ? suppliers.find(s => s.id === product.vendorId) : null;
    const vName = vendor?.storeName || supplier?.tradeName || supplier?.companyName || 'Parceiro';

    const otherShop = cartVendorId != null && cartVendorId !== product.vendorId;
    if (otherShop && cart.length > 0) {
      toast.info(
        'Carrinho atualizado',
        'Você só pode comprar de uma loja por pedido. Limpamos o carrinho anterior.'
      );
    }

    setCart((prev) => {
      const base = otherShop && prev.length > 0 ? [] : prev;
      return [
        ...base,
        {
          ...product,
          quantity,
          vendorName: vName,
          notes: customization.note,
          selectedSize: customization.selectedSize,
          selectedColor: customization.selectedColor,
        },
      ];
    });
    setCartVendorId(product.vendorId);
    setCustomizingProduct(null);
    toast.success('Adicionado!', `${typeof product.name === 'string' ? product.name : product.name?.pt || 'Produto'} no carrinho.`);
    setIsCartOpen(true);
  };

  const handleCheckout = async (orderDetails: any) => {
    const orderId = `order-${Date.now()}`;
    const subtotal = orderDetails.subtotal ?? cart.reduce((acc, i) => acc + (i.price * i.quantity), 0);
    const deliveryFee = orderDetails.deliveryFee ?? DELIVERY_CONFIG.BASE_FEE;
    const resolvedVendorId = cart[0]?.vendorId || 'vendor_jose_01';

    const orderSupplier = suppliers.find((s) => s.id === resolvedVendorId);
    if (orderSupplier) {
      if (!logStorePresenceCheck('checkout-supplier', orderSupplier)) {
        toast.error('Fornecedor fechado', SUPPLIER_STORE_CLOSED_MESSAGE);
        return;
      }
    } else {
      const orderVendorCheck = vendors.find(v => v.id === resolvedVendorId);
      if (!isVendorStoreLive(orderVendorCheck)) {
        toast.error('Loja fechada', VENDOR_STORE_CLOSED_MESSAGE);
        return;
      }
    }

    const orderVendor = vendors.find(v => v.id === resolvedVendorId);
    const orderSupplierResolved = orderSupplier;

    const localOrder: Order = {
      id: orderId,
      orderNumber: orderId.slice(-6).toUpperCase(),
      vendorId: resolvedVendorId,
      vendorUid: resolvedVendorId,
      vendorName:
        orderVendor?.storeName ||
        orderVendor?.name ||
        orderSupplierResolved?.tradeName ||
        orderSupplierResolved?.companyName ||
        '',
      clientUid: authUserId || undefined,
      vendorPhone: orderVendor?.phone || orderSupplierResolved?.phone || '',
      customerName: orderDetails.name.trim(),
      customerPhone: orderDetails.phone.trim(),
      items: [...cart],
      deliveryNote: orderDetails.note?.trim() || '',
      paymentMethod: orderDetails.payment,
      subtotal,
      deliveryFee,
      total: subtotal + deliveryFee,
      status: 'pending',
      timestamp: Date.now(),
      needsDriver: true,
      distance: orderDetails.distance
    };

    actions.addOrder(localOrder);
    
    // LIMPEZA COMPLETA após checkout (sugestão Sonnet)
    setCart([]);
    setCartVendorId(null);
    setCustomizingProduct(null); // Limpar produto em customização
    setIsCartOpen(false);
    
    // Limpar cache do navegador
    try {
      sessionStorage.removeItem('pendingOrder');
      sessionStorage.removeItem('cartItems');
      clearCartSession();
    } catch (e) {
      // sessionStorage pode não estar disponível
    }
    
    setOrderSuccess({ show: true, order: localOrder });
    log.info('? [Checkout] Pedido criado e estado limpo:', orderId);
  };

  const loginWithCredentials = async (email: string, password: string) => {
    if (!email || !password) return;

    setIsLoggingIn(true);
    const res = await authService.login(email, password);
    setIsLoggingIn(false);

    if (res.success && res.userId && res.role) {
      const effectiveRole = await resolveEffectiveUserRole(res.userId, res.role);
      log.info('Login bem-sucedido:', { role: effectiveRole, name: res.name });

      // Aplica o status lido do Firestore imediatamente, antes de qualquer render
      // Isso garante que o guard pending_approval bloqueie antes do onSnapshot reagir
      if (res.userStatus) {
        setCurrentUserStatus(res.userStatus);
      }

      if (effectiveRole === 'VENDOR') {
        void syncUserRoleIfVendor(res.userId, 'VENDOR', res.role);
        const linked = await resolveVendorByAuthUid(res.userId);
        if (linked) {
          setResolvedVendorFallback(linked);
          setVendorLinkState('linked');
          if (!state.vendors.some((v) => v.id === linked.id)) {
            actions.addVendor(linked);
          }
        } else {
          setVendorLinkState('missing');
        }
      }
      
      // Se for VENDOR, garantir sincronização com Firestore (CRÍTICO para checkout)
      if (effectiveRole === 'VENDOR') {
        const existingVendor = state.vendors.find(v => v.id === res.userId);
        if (!existingVendor) {
          log.info('Criando vendor para novo usuário...');
          
          // Buscar perfil completo do Firebase para pegar categoria
          let userCategory: VendorCategory | undefined;
          try {
            const userDoc = await getDoc(doc(db, 'users', res.userId));
            if (userDoc.exists()) {
              userCategory = userDoc.data().category as VendorCategory;
              log.info('Categoria encontrada no perfil:', userCategory);
            }
          } catch (err) {
            log.warn('Erro ao buscar categoria:', err);
          }
          
          const newVendor = {
            id: res.userId,
            name: res.name || 'Novo Vendedor',
            ownerName: res.name || 'Proprietário',
            storeName: res.name || 'Minha Loja',
            email: email.toLowerCase().trim(),
            phone: '',
            image: 'https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=400',
            location: null,
            locationStatus: 'PENDING_ADDRESS',
            vendorStatus: res.vendorStatus || 'UNREGULATED',
            subscriptionStatus: 'active',
            regionalId: res.regionalId,
            rating: 5.0,
            category: userCategory,
            deliveryMode: getClientDefaultDeliveryMode(),
          } as Vendor;
          actions.addVendor(newVendor);
          log.info('? Vendor criado com categoria');
          
          // Salvar no Firebase vendors collection
          try {
            await setDoc(doc(db, 'vendors', res.userId), removeUndefined(newVendor), { merge: true });
            log.info('? Vendor salvo no Firebase');
          } catch (fbErr) {
            log.warn('Erro ao salvar vendor no Firebase:', fbErr);
          }
          
          // Sincroniza role/email no users/{id} sem sobrescrever userStatus
          // (userStatus é controlado pelo Owner via fila de aprovação)
          try {
            await setDoc(doc(db, 'users', res.userId), {
              email: email.toLowerCase().trim(),
              name: newVendor.name,
              role: 'VENDOR',
              updatedAt: Date.now()
            }, { merge: true });
            log.info('? users/{id} sincronizado com role VENDOR');
          } catch (fbErr) {
            log.warn('Erro ao sincronizar users/{id}:', fbErr);
          }
        } else {
          // FIX CRÍTICO: Vendedor existente - garantir que está no Firestore
          // Isso corrige o problema de "Vendedor não encontrado" no checkout
          try {
            const { payment: _pixDaLoja, ...vendorSemPix } = existingVendor;
            await setDoc(doc(db, 'vendors', res.userId), removeUndefined(omitServerOnlyStoreFields('vendors', vendorSemPix)), { merge: true });
            log.info('Vendor sincronizado com Firestore');
          } catch (fbErr) {
            log.warn('Erro ao sincronizar vendor:', fbErr);
          }
          
          // Sincroniza role/email no users/{id} sem sobrescrever userStatus
          // (userStatus é controlado pelo Owner via fila de aprovação)
          try {
            await setDoc(doc(db, 'users', res.userId), {
              email: existingVendor.email || email.toLowerCase().trim(),
              name: existingVendor.name,
              role: 'VENDOR',
              updatedAt: Date.now()
            }, { merge: true });
          } catch {}
        }
        
        // FIX CRÍTICO: Sincronizar produtos do vendedor para Firestore
        // Cloud Function precisa dos produtos para calcular preços (segurança)
        const vendorProducts = state.products.filter(p => p.vendorId === res.userId);
        if (vendorProducts.length > 0) {
          log.info(`Sincronizando ${vendorProducts.length} produtos...`);
          for (const product of vendorProducts) {
            try {
              const enriched = enrichProductL6Fields(product);
              await setDoc(doc(db, 'products', product.id), removeUndefined(enriched), { merge: true });
            } catch {
              // Erro silencioso para não travar login
            }
          }
          log.info('? Produtos sincronizados');
        }
      }
      
      // Se for DRIVER: garantir doc drivers/{uid}. Fonte da verdade = Firestore.
      // NÃO sobrescrever estado operacional (isOnline/status/userStatus) de cadastro existente.
      if (res.role === 'DRIVER') {
        // 1) Consulta o Firestore primeiro (state.drivers fica vazio para DRIVER — ver AppContext)
        let existingDoc: Driver | null = null;
        try {
          const snap = await getDoc(doc(db, 'drivers', res.userId));
          if (snap.exists()) {
            existingDoc = { id: snap.id, ...snap.data() } as Driver;
          }
        } catch (fbErr) {
          log.warn('Erro ao buscar driver no Firebase:', fbErr);
        }

        const existingDriver = existingDoc || state.drivers.find(d => d.id === res.userId);

        if (!existingDriver) {
          // 2) Cria SOMENTE quando não existe em lugar nenhum
          log.info('Criando driver automaticamente...');
          const newDriver: Driver = {
            id: res.userId,
            authUserId: res.userId,
            name: res.name || 'Novo Entregador',
            email: email.toLowerCase().trim(),
            phone: '',
            cpf: '',
            cnh: '',
            vehicleType: 'motorcycle',
            vehicle: 'Honda CG 160',
            plate: '',
            isOnline: false,
            status: 'offline',
            userStatus: (res.userStatus as Driver['userStatus']) || 'pending_approval',
            earnings: 0,
            totalDeliveries: 0,
            rating: 5.0,
          };

          actions.addDriver(newDriver);
          log.info('Driver criado:', newDriver.name);

          try {
            await setDoc(doc(db, 'drivers', res.userId), removeUndefined(newDriver), { merge: true });
            log.info('Driver salvo no Firebase');
          } catch (fbErr) {
            log.warn('Erro ao salvar driver no Firebase:', fbErr);
          }
        } else {
          // 3) Doc já existe: reidrata o state SEM sobrescrever estado operacional.
          //    addDriver usa merge e reescreve os campos lidos (idempotente) + updatedAt.
          if (existingDoc) {
            actions.addDriver(existingDoc);
          }
          log.info('Driver já existe, reidratado:', existingDriver.name);
        }

        // 4) Sincroniza users/{id} (role + nome + email) — NUNCA toca userStatus
        try {
          await setDoc(doc(db, 'users', res.userId), {
            email: email.toLowerCase().trim(),
            name: existingDriver?.name || res.name || 'Novo Entregador',
            role: 'DRIVER',
            updatedAt: Date.now()
          }, { merge: true });
        } catch (fbErr) {
          log.warn('Erro ao sincronizar users/{id}:', fbErr);
        }
      }
      
      setAuthUserId(res.userId);
      setActiveRole(effectiveRole);
      setIsLoginModalOpen(false);
      setLoginEmail('');
      setLoginPassword('');
      // Beep de confirmação de login (sem voz)
      audioService.playAlert('soft').catch(() => {});
    } else {
      toast.error('Erro ao entrar', res.message);
    }
  };

  const handleLoginSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    await loginWithCredentials(loginEmail, loginPassword);
  };

  const handleOpenAgoraPreview = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (publicOrderId) return;
    try {
      pushUrlAgoraView();
    } catch (e) {
      console.error('[Ágora] pushState (?view=agora):', e);
    }
    setClientView('agora');
  }, [publicOrderId]);

  const handleCloseAgoraPreview = useCallback(() => {
    try {
      const st = window.history.state as Record<string, string | undefined> | null;
      if (st?.[AGORA_HIST_KEY] === AGORA_URL_PARAM) {
        window.history.back();
        return;
      }
    } catch {
      /* usar fallback */
    }
    stripAgoraFromUrlPreserveParams();
    setClientView('home');
  }, []);

  // Atalho para testes: abre diretamente o modal de cadastro se ?register=1
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('register') === '1') {
      setIsRegisterModalOpen(true);
      setIsLoginModalOpen(false);
    }

    // autoLogin via URL removido por segurança (credenciais não devem trafegar em URL)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseCreditPreview = useCallback(() => {
    try {
      const st = window.history.state as Record<string, unknown> | null;
      if (st?.[CREDIT_PREVIEW_HIST_KEY]) {
        window.history.back();
        return;
      }
    } catch {
      /* usar fallback */
    }
    try {
      window.history.replaceState(window.history.state, '', '/');
    } catch {
      /* ignore */
    }
    setIsCreditPreviewRoute(false);
    setClientView('home');
  }, []);

  const handleOpenCreditPreview = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (publicOrderId) return;
    setIsLoginModalOpen(false);
    setIsPartnerCreditRoute(false);
    setIsCreditPreviewRoute(true);
    try {
      window.history.pushState({ [CREDIT_PREVIEW_HIST_KEY]: true }, '', CREDIT_PREVIEW_PATH);
    } catch (e) {
      console.error('[Credit Preview] pushState (/credit-preview):', e);
      window.location.assign(CREDIT_PREVIEW_PATH);
    }
  }, [publicOrderId]);

  const handleOpenPartnerCredit = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (publicOrderId) return;
    setIsLoginModalOpen(false);
    setIsCreditPreviewRoute(false);
    setIsPartnerCreditRoute(true);
    try {
      window.history.pushState({ [PARTNER_CREDIT_HIST_KEY]: true }, '', PARTNER_CREDIT_PATH);
    } catch (e) {
      console.error('[Partner Credit] pushState (/partner-credit):', e);
      window.location.assign(PARTNER_CREDIT_PATH);
    }
  }, [publicOrderId]);

  const handleClosePartnerCredit = useCallback(() => {
    try {
      const st = window.history.state as Record<string, unknown> | null;
      if (st?.[PARTNER_CREDIT_HIST_KEY]) {
        window.history.back();
        return;
      }
    } catch {
      /* usar fallback */
    }
    try {
      window.history.replaceState(window.history.state, '', '/');
    } catch {
      /* ignore */
    }
    setIsPartnerCreditRoute(false);
  }, []);

  // Rota /credit-preview e /partner-credit — sincroniza com voltar do navegador
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncCreditRoutes = () => {
      setIsCreditPreviewRoute(isCreditPreviewPathname());
      setIsPartnerCreditRoute(isPartnerCreditPathname());
    };
    window.addEventListener('popstate', syncCreditRoutes);
    return () => window.removeEventListener('popstate', syncCreditRoutes);
  }, []);

  // /partner-credit exige auth + OWNER|SUPER_ADMIN — sem permissão, volta ao destino padrão
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isCheckingAuth) return;
    if (!isPartnerCreditRoute && !isPartnerCreditPathname()) return;
    const allowed =
      !!authUserId && (activeRole === 'OWNER' || activeRole === 'SUPER_ADMIN');
    if (allowed) return;
    try {
      window.history.replaceState(window.history.state, '', '/');
    } catch {
      /* ignore */
    }
    setIsPartnerCreditRoute(false);
  }, [isCheckingAuth, authUserId, activeRole, isPartnerCreditRoute]);

  // Voltar navegador: sincroniza loja/diretório/ágora com o histórico (não ejeta do SPA).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPopState = (event: PopStateEvent) => {
      try {
        if (publicOrderId) return;
        if (/^\/pedido\//.test(window.location.pathname)) return;
        if (isCreditPreviewPathname()) return;
        if (isPartnerCreditPathname()) return;

        const nav = event.state as EambClientNavState | null;
        if (nav && nav[EAMB_CLIENT_NAV] === 1) {
          skipClientNavPushRef.current = true;
          setClientView(nav.view);
          setActiveVendorId(nav.vendorId);
          setActiveSupplierId(nav.supplierId);
          return;
        }

        const wants = urlRequestsAgoraPreview();
        setClientView((prev) => {
          if (wants) return 'agora';
          if (prev === 'agora') return 'home';
          return prev;
        });
      } catch {
        /* ignore */
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [publicOrderId]);

  // Saiu da prévia pela UI (ex.: Explorar) mas URL ainda tinha ?view= — alinha só com replaceState.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (publicOrderId) return;
    try {
      if (clientView !== 'agora' && urlRequestsAgoraPreview()) {
        stripAgoraFromUrlPreserveParams();
      }
    } catch {
      /* ignore */
    }
  }, [clientView, publicOrderId]);

  // Loja amigável inexistente — não exige login.
  if (storeSlugNotFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl bg-white border border-slate-200 p-8 text-center shadow-sm">
          <h1 className="text-2xl font-black text-slate-800">Loja não encontrada</h1>
          <p className="mt-3 text-slate-600">Confira o endereço ou fale com a loja pelo WhatsApp.</p>
          <button onClick={() => window.location.assign('/')} className="mt-6 min-h-[44px] rounded-xl bg-emerald-600 px-5 text-white font-bold">Página inicial</button>
        </div>
      </div>
    );
  }

  // Rastreio público /pedido/:id — prioridade sobre loading de auth (sem login)
  if (publicOrderId) {
    return (
      <Suspense fallback={<DashboardLoader />}>
        <PublicOrderTrackingPage orderId={publicOrderId} />
      </Suspense>
    );
  }

  // Prévia de crédito — demonstração fictícia /credit-preview (antes do gate de auth)
  if (isCreditPreviewRoute || isCreditPreviewPathname()) {
    return (
      <Suspense fallback={<DashboardLoader />}>
        <CreditPreviewPanel demoMode onBack={handleCloseCreditPreview} />
      </Suspense>
    );
  }

  // OS05-S2 — link mágico Pix (público, sem login)
  if (isPixKeyInvitePathname()) {
    return (
      <Suspense fallback={<DashboardLoader />}>
        <PublicPixKeyPage />
      </Suspense>
    );
  }

  // P0 — cadastro mobile /v/:campaign (só com flag; senão redireciona ao legado)
  if (isMobileRegisterPathname()) {
    if (!isMobileRegisterEnabledClient()) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 bg-emerald-50">
          <p className="text-slate-800 font-semibold text-center">
            O cadastro rápido por link ainda não está liberado neste ambiente.
          </p>
          <button
            type="button"
            className="px-4 py-3 rounded-xl bg-slate-900 text-white font-bold"
            onClick={() => {
              window.history.replaceState({}, '', '/');
              window.location.assign('/');
            }}
          >
            Voltar ao início
          </button>
        </div>
      );
    }
    return (
      <Suspense fallback={<DashboardLoader />}>
        <MobileRegistrationFlow
          campaign={parseMobileRegisterCampaign()}
          onClose={() => window.location.assign('/')}
        />
      </Suspense>
    );
  }

  // TELA DE LOADING ENQUANTO VERIFICA AUTENTICAÇÃO
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-emerald-700 font-bold">Carregando...</p>
        </div>
      </div>
    );
  }

  // Painel parceiro LIVE /partner-credit — só após auth; guard OWNER | SUPER_ADMIN
  if (
    (isPartnerCreditRoute || isPartnerCreditPathname()) &&
    authUserId &&
    (activeRole === 'OWNER' || activeRole === 'SUPER_ADMIN')
  ) {
    return (
      <Suspense fallback={<DashboardLoader />}>
        <CreditPreviewPanel demoMode={false} onBack={handleClosePartnerCredit} />
      </Suspense>
    );
  }

  // DASHBOARDS PROFISSIONAIS
  
  if (currentUserStatus === 'pending_approval' && (activeRole === 'VENDOR' || activeRole === 'SUPPLIER' || activeRole === 'DRIVER' || activeRole === 'CITY_HALL')) {
    const pendingInfo: Record<string, { icon: string; title: string; subtitle: string; detail: string }> = {
      VENDOR:    { icon: '🏪', title: 'Loja em análise',           subtitle: 'Recebemos seu cadastro de vendedor.',       detail: 'Nosso time verificará suas informações antes de liberar o painel.' },
      SUPPLIER:  { icon: '📦', title: 'Fornecedor em análise',     subtitle: 'Recebemos seu cadastro de fornecedor.',     detail: 'Nosso time verificará suas informações antes de liberar o acesso.' },
      DRIVER:    { icon: '🛵', title: 'Motoboy em análise',       subtitle: 'Recebemos seu cadastro de entregador.',     detail: 'Nosso time irá verificar seus documentos (CNH, CPF, veículo) antes de liberar.' },
      CITY_HALL: { icon: '🏛️', title: 'Credencial em análise',    subtitle: 'Recebemos sua solicitação de acesso.',      detail: 'O administrador da plataforma validará sua matrícula e vinculação institucional antes de liberar o acesso ao painel de fiscalização.' },
    };
    const info = pendingInfo[activeRole] ?? pendingInfo.VENDOR;
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">{info.icon}</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">{info.title}</h1>
          <p className="text-slate-500 mb-2">{info.subtitle}</p>
          <p className="text-sm text-slate-400 mb-6">{info.detail}</p>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 text-left space-y-2">
            <p className="text-sm font-semibold text-amber-800">O que acontece agora?</p>
            <p className="text-sm text-amber-700">Seus dados foram salvos com sucesso</p>
            <p className="text-sm text-amber-700">Nossa equipe está analisando seu perfil</p>
            <p className="text-sm text-amber-700">Esta tela atualiza automaticamente quando aprovado</p>
          </div>
          <p className="text-xs text-slate-400 mb-6">
            Tempo médio de aprovação: até 24 horas úteis
          </p>
          <button
            onClick={async () => {
              await authService.logout();
              setAuthUserId(null);
              setActiveRole('CLIENT');
              setCurrentUserStatus('active');
            }}
            className="w-full bg-slate-100 text-slate-700 py-3 px-6 rounded-xl font-semibold hover:bg-slate-200 transition-colors"
          >
            Sair
          </button>
        </div>
      </div>
    );
  }

  // ── GUARDA: usuário suspenso ou com credencial revogada ──
  if (
    (currentUserStatus === 'suspended' || currentUserStatus === 'revoked') &&
    activeRole !== 'OWNER' && activeRole !== 'CLIENT'
  ) {
    const suspendedInfo: Record<string, { icon: string; title: string; detail: string }> = {
      VENDOR:    { icon: '🏪', title: 'Loja suspensa',              detail: 'Sua conta foi suspensa pelo administrador. Entre em contato para regularizar sua situação.' },
      SUPPLIER:  { icon: '📦', title: 'Fornecedor suspenso',        detail: 'Sua conta foi suspensa pelo administrador. Entre em contato para regularizar sua situação.' },
      DRIVER:    { icon: '🛵', title: 'Conta do entregador suspensa', detail: 'Seu acesso foi suspenso pelo administrador. Entre em contato para mais informações.' },
      CITY_HALL: { icon: '🏛️', title: 'Credencial revogada',        detail: 'Sua credencial institucional foi revogada. Caso seja um engano, entre em contato com o administrador da plataforma.' },
    };
    const info = suspendedInfo[activeRole] ?? { icon: '🔒', title: 'Acesso suspenso', detail: 'Sua conta foi suspensa. Entre em contato com o suporte.' };
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">{info.icon}</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">{info.title}</h1>
          <p className="text-sm text-slate-500 mb-6">{info.detail}</p>
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 text-left">
            <p className="text-sm font-semibold text-red-800">Status da conta</p>
            <p className="text-sm text-red-700 mt-1">
              {currentUserStatus === 'revoked' ? 'Credencial permanentemente revogada' : 'Conta temporariamente suspensa'}
            </p>
          </div>
          <button
            onClick={async () => {
              await authService.logout();
              setAuthUserId(null);
              setActiveRole('CLIENT');
              setCurrentUserStatus('active');
            }}
            className="w-full bg-slate-100 text-slate-700 py-3 px-6 rounded-xl font-semibold hover:bg-slate-200 transition-colors"
          >
            Sair
          </button>
        </div>
      </div>
    );
  }

  // VENDOR sem loja vinculada no Firestore
  if (activeRole === 'VENDOR' && authUserId && !currentVendor) {
    const warnKey = `${authUserId}:${vendors.length}:${vendorLinkState}`;
    if (vendorLinkState !== 'loading' && vendorWarnRef.current !== warnKey) {
      vendorWarnRef.current = warnKey;
      console.warn('[VENDOR] Loja nao encontrada!');
      console.log('   - authUserId:', authUserId);
      console.log('   - vendors carregados:', vendors.length);
      console.log('   - vendorLinkState:', vendorLinkState);
    }

    if (vendorLinkState === 'missing') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-4">
          <div className="text-center bg-white p-8 rounded-3xl shadow-xl max-w-md">
            <p className="text-amber-800 font-bold text-lg mb-2">Loja nao vinculada</p>
            <p className="text-slate-600 text-sm mb-6">
              Sua loja ainda nao foi vinculada corretamente ao seu login. Fale com o suporte ou aguarde a
              conclusao da aprovacao.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-emerald-500 text-white py-3 px-6 rounded-xl font-bold hover:bg-emerald-600"
              >
                Tentar novamente
              </button>
              <button
                onClick={async () => {
                  await authService.logout();
                  setAuthUserId(null);
                  setActiveRole('CLIENT');
                  localStorage.removeItem('eambulante_auth_state');
                }}
                className="w-full bg-slate-100 text-slate-700 py-3 px-6 rounded-xl font-medium hover:bg-slate-200"
              >
                Sair
              </button>
              <p className="text-[10px] text-slate-400">ID: {authUserId.slice(0, 8)}...</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="text-center bg-white p-8 rounded-3xl shadow-xl max-w-md">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-emerald-700 font-bold mb-2">Configurando sua loja...</p>
          <p className="text-slate-500 text-sm mb-4">Aguarde um momento</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 w-full bg-slate-100 text-slate-700 py-3 px-6 rounded-xl font-medium hover:bg-slate-200"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }
  
  if (activeRole === 'VENDOR' && currentVendor && vendorLinkState === 'linked') {
    return (
      <Suspense fallback={<DashboardLoader />}>
        <div className={scrollClasses}>
          <VendorDashboard 
            vendor={currentVendor}
            products={scopedVendorDash.products}
            orders={state.orders.filter(o => o.vendorId === currentVendor.id)}
            onAddProduct={actions.addProduct}
            onUpdateProduct={actions.updateProduct}
            onUpdateVendor={actions.updateVendor}
            onRemoveProduct={actions.removeProduct}
            onUpdateOrderStatus={actions.updateOrderStatus}
            onToggleProductStatus={(id) => {
              const product = scopedVendorDash.products.find(p => p.id === id);
              if (product) {
                const nextPaused = !product.isPaused;
                actions.updateProduct({
                  ...product,
                  isPaused: nextPaused,
                  available: !nextPaused,
                });
              }
            }}
            onLogout={async () => { 
              await authService.logout();
              setAuthUserId(null); 
              setActiveRole('CLIENT'); 
            }}
            onRequestDriver={(orderId) => {
              const order = state.orders.find((o) => o.id === orderId);
              const gate = assertCanDispatchOrder(order || {});
              if (!gate.ok) {
                toast.error('Despacho bloqueado', gate.message);
                return;
              }
              actions.updateOrderStatus(orderId, 'ready_for_pickup');
            }}
            onPaySubscription={() => {}}
            onCancelOrder={() => {}}
          />
        </div>
      </Suspense>
    );
  }

  if (activeRole === 'DRIVER') {
    // Busca apenas no state (Firebase = fonte da verdade)
    const driver = drivers.find(d => d.id === authUserId);
    
    // Aguardar carregamento inicial antes de exibir erro - evita race condition
    if (!driver && state.isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-600 font-medium">Carregando perfil...</p>
          </div>
        </div>
      );
    }

    // Só exibe erro quando o carregamento terminou e o driver realmente não existe
    if (!driver) {
      log.error(`? Driver não encontrado: authUserId=${authUserId}`);
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
          <div className="bg-white rounded-3xl p-8 shadow-xl text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
              <AlertTriangle className="w-8 h-8" aria-hidden />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Cadastro de motoboy incompleto</h2>
            <p className="text-slate-600 mb-6">
              Seu cadastro de entregador não foi localizado no sistema. Contate o suporte para liberar sua conta.
            </p>
            <p className="text-xs text-slate-400 mb-4">ID: {authUserId}</p>
            <button 
              onClick={async () => { 
                await authService.logout(); 
                setAuthUserId(null); 
                setActiveRole('CLIENT'); 
              }}
              className="bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors"
            >
              Voltar ao Login
            </button>
          </div>
        </div>
      );
    }
    
    // TRANSAÇÃO ATÔMICA: Concluir entrega (FIX: sem dupla escrita)
    const handleCompleteDelivery = async (orderId: string): Promise<boolean> => {
      const currentDriver = drivers.find(d => d.id === authUserId);
      if (!currentDriver) return false;

      // Executa transação atômica no Firebase PRIMEIRO
      const result = await completeOrderTransaction(orderId, currentDriver.id);

      if (result.success) {
        // Transação OK — Firebase já gravou delivered
        // NÃO chamar updateOrderStatus — onSnapshot sincroniza (mesmo padrão do accept)

        const order = state.orders.find(o => o.id === orderId);

        if (order) {
          // FIX: prioriza driverEarnings, aceita deliveryFee = 0
          const fee = order.driverEarnings ?? order.deliveryFee ?? 0;
          const currentEarnings = currentDriver.earnings || 0;
          const newEarnings = currentEarnings + fee;

          actions.updateDriver({
            ...currentDriver,
            earnings: newEarnings,
            totalDeliveries: (currentDriver.totalDeliveries || 0) + 1
          });

          log.info(`Entrega concluída! Motoboy ${currentDriver.name}: R$ ${currentEarnings.toFixed(2)} → R$ ${newEarnings.toFixed(2)} (+R$ ${fee.toFixed(2)})`);
        }

        // Toast com o MESMO valor que as telas do motoboy mostram (getDriverPayout: piso R$ 5).
        // Se o pedido não estiver no estado local, celebra sem valor — nunca "R$ 0.00".
        if (order) {
          const payout = order.driverEarnings ?? order.deliveryFee;
          const display = (payout != null && payout > 0) ? payout : 5;
          toast.success('Entrega concluída!', `Você ganhou R$ ${display.toFixed(2)}`);
        } else {
          toast.success('Entrega concluída!');
        }
        audioService.vibrate([100, 50, 100]);
        audioService.playAlert('soft').catch(() => {});
        return true;
      }

      // Transação falhou — NÃO atualizar local
      log.error('? Erro ao concluir entrega:', result.error);
      if (result.error === 'NOT_YOUR_ORDER') {
        toast.warning('Pedido indisponível', 'Este pedido não está mais sob sua responsabilidade.');
      } else if (result.error === 'INVALID_STATUS') {
        toast.warning('Não foi possível', 'Este pedido não pode ser concluído agora.');
      } else {
        toast.error('Erro na entrega', result.errorMessage || 'Tente novamente em instantes.');
      }
      return false;
    };


    return (
      <Suspense fallback={<DashboardLoader />}>
        <div className={scrollClasses}>
          <DriverDashboard 
            driver={driver}
            availableOrders={state.orders.filter(o => o.status === 'ready_for_pickup' && !o.driverId)}
            onAcceptOrder={async (orderId) => {
              // TRANSAÇÃO ATÔMICA: Firebase é a ÚNICA fonte de verdade
              // Após transação, o listener onSnapshot atualiza automaticamente
              console.log('[AcceptOrder] Iniciando aceite:', { orderId, driverId: driver.id, driverName: driver.name });
              
              const result = await acceptOrderTransaction(orderId, driver.id, driver.name);
              
              console.log('[AcceptOrder] Resultado transação:', result);
              
              if (result.success) {
                console.log('[AcceptOrder] Sucesso! Aguardando sync do Firebase...');
                audioService.playAlert('soft').catch(() => {});
              } else {
                log.error('Erro ao aceitar pedido:', result.error);
                
                if (result.error === 'ALREADY_ACCEPTED') {
                  toast.warning(
                    'Essa entrega já foi aceita por outro motoboy.',
                    'Atualizamos a lista — tente outra corrida disponível.'
                  );
                } else if (result.error === 'INVALID_STATUS') {
                  toast.warning('Indisponível', 'Este pedido não está mais disponível para coleta.');
                } else if (result.error === 'ORDER_NOT_FOUND') {
                  toast.error('Pedido não encontrado', 'Atualize a lista e tente novamente.');
                } else if (result.error === 'ORDER_EXPIRED' || result.error === 'PERMISSION_DENIED') {
                  toast.warning(
                    'Entrega expirada',
                    'Este pedido saiu do pool. Aguarde novas corridas.'
                  );
                } else {
                  toast.error('Erro ao aceitar', 'Tente novamente em instantes.');
                }
              }
              return result;
            }}
            onCompleteOrder={handleCompleteDelivery}
            onToggleStatus={() => {
              // Iniciar/Pausar turno — grava isOnline no Firestore (sem tocar FCM unregister)
              actions.updateDriver({
                ...driver,
                isOnline: !driver.isOnline,
                status: (!driver.isOnline ? 'available' : 'offline') as
                  | 'available'
                  | 'offline'
                  | 'busy',
              });
            }}
            onForceOffline={() => {
              // Auto-pause após rede/aba dormindo — NÃO chama unregisterDriverFCM
              if (!driver.isOnline) return;
              actions.updateDriver({
                ...driver,
                isOnline: false,
                status: 'offline' as const,
              });
            }}
            onLogout={async () => { await authService.logout(); setAuthUserId(null); setActiveRole('CLIENT'); }}
          />
        </div>
      </Suspense>
    );
  }

  if (activeRole === 'CITY_HALL') {
    // Aguardar carregamento do perfil institucional
    if (!cityHallProfile) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm font-bold text-slate-600">Carregando perfil institucional...</p>
            <p className="text-xs text-slate-400">Conectando ao painel do gestor municipal</p>
          </div>
        </div>
      );
    }

    // Tenant SaaS: municipalUsers.orgId (piloto Abul2 etc.) — fallback legado só se ausente
    const municipalOrgId =
      municipalTenantOrgId || `org_${cityHallProfile.cityHallOrg || 'MUNICIPAL'}`;

    if (showGovTechMunicipal) {
      return (
        <Suspense fallback={<DashboardLoader />}>
          <MunicipalGovDashboard
            orgId={municipalOrgId}
            onBack={() => setShowGovTechMunicipal(false)}
          />
        </Suspense>
      );
    }

    return (
      <Suspense fallback={<DashboardLoader />}>
        <div className={scrollClasses}>
          <div className="bg-teal-800 text-white text-xs px-3 py-2 flex justify-between items-center">
            <span>GovTech Municipal (Fase 1) disponível — módulo por flag, sem bloquear vendas</span>
            <button
              type="button"
              className="underline font-semibold"
              onClick={() => setShowGovTechMunicipal(true)}
            >
              Abrir módulo
            </button>
          </div>
          <CityHallDashboard 
            vendors={vendors}
            cityHallAccount={cityHallProfile!}
            onLogout={async () => { await authService.logout(); setAuthUserId(null); setActiveRole('CLIENT'); }}
            onApproveVendor={(_vendorId, _permitNumber) => {
              // Fluxo correto: Secretário Regional emite o TPU via Autorização Regional.
              // Este callback não é mais chamado com efeito; handleAprovar redireciona
              // o fiscal para a aba correta antes de chegar aqui.
            }}
            onSuspendVendor={() => {
              toast.info(
                'Termo',
                'Suspender o termo fica para a Fase 3. A loja de vendas não é alterada.'
              );
            }}
            onRevokeVendor={() => {
              toast.info(
                'Termo',
                'Cancelar o termo fica para a Fase 3. A loja de vendas não é alterada.'
              );
            }}
          />
        </div>
      </Suspense>
    );
  }


  if (activeRole === 'SUPPLIER') {
    // Suppliers têm coleção própria - nunca buscar em vendors[]
    const supplier = suppliers.find(s => s.id === authUserId)
      ?? suppliers.find(s => s.uid === authUserId)
      ?? suppliers.find(s => s.email === auth.currentUser?.email)
      ?? null;

    // Se suppliers ainda está carregando, mostrar spinner em vez de "não encontrado"
    if (!supplier && state.isLoading) {
      return <DashboardLoader />;
    }

    // Se suppliers carregou mas está vazio, tentar buscar direto do Firestore
    if (!supplier && !state.isLoading && suppliers.length === 0) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center p-8 bg-white rounded-2xl shadow-sm max-w-sm">
            <p className="text-4xl mb-4">?</p>
            <h2 className="font-black text-slate-800 text-lg mb-2">
              Carregando perfil...
            </h2>
            <p className="text-slate-500 text-sm mb-6">
              Sincronizando dados do fornecedor.
            </p>
            <button
              onClick={async () => {
                await authService.logout();
                setAuthUserId(null);
                setActiveRole('CLIENT');
              }}
              className="bg-brand-green text-white px-6 py-3 rounded-xl font-bold w-full"
            >
              Voltar ao in?cio
            </button>
          </div>
        </div>
      );
    }

    if (!supplier) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center p-8 bg-white rounded-2xl shadow-sm max-w-sm">
            <p className="text-4xl mb-4">📦</p>
            <h2 className="font-black text-slate-800 text-lg mb-2">
              Perfil de Fornecedor n?o encontrado
            </h2>
            <p className="text-slate-500 text-sm mb-6">
              Seu cadastro pode estar sendo processado.
            </p>
            <button
              onClick={async () => {
                await authService.logout();
                setAuthUserId(null);
                setActiveRole('CLIENT');
              }}
              className="bg-brand-green text-white px-6 py-3 rounded-xl font-bold w-full"
            >
              Voltar ao in?cio
            </button>
          </div>
        </div>
      );
    }

    return (
      <Suspense fallback={<DashboardLoader />}>
        <div className={scrollClasses}>
          <SupplierDashboard
            supplier={supplier}
            products={scopedSupplierDash.products}
            orders={state.orders.filter(o =>
              o.vendorId === supplier.id ||
              (o as any).vendorUid === supplier.id ||
              (o as any).vendorUid === authUserId ||
              o.vendorId === authUserId
            )}
            onAddProduct={actions.addProduct}
            onUpdateProduct={actions.updateProduct}
            onUpdateSupplier={actions.updateSupplier}
            onRemoveProduct={actions.removeProduct}
            onToggleProductStatus={async (id) => {
              // F2-ADAPTER: find no cache scoped (não depende do global)
              const p = scopedSupplierDash.products.find(pr => pr.id === id);
              const nomeAtual =
                typeof p?.name === 'string'
                  ? p.name
                  : p?.name?.pt || '';
              const isPausedAtual = p?.isPaused;
              console.log('[togglePause] click', { id, nomeAtual, isPausedAtual });

              if (!p?.id) {
                console.error(
                  '[togglePause] FAIL',
                  'not-found',
                  'produto não encontrado na lista scoped',
                  { id }
                );
                toast.error(
                  'Não foi possível alterar o status. Tente novamente.'
                );
                return;
              }

              const nextPaused = !p.isPaused;
              // Fonte de verdade: isPaused; available espelhado p/ loja (OS 03-B)
              const payload = { ...p, isPaused: nextPaused, available: !nextPaused };
              console.log('[togglePause] payload', payload);

              try {
                await actions.updateProduct(payload);
                console.log('[togglePause] OK', id);
              } catch (err: unknown) {
                const e = err as { code?: string; message?: string };
                console.error('[togglePause] FAIL', e?.code, e?.message, err);
                toast.error(
                  'Não foi possível alterar o status. Tente novamente.'
                );
              }
            }}
            onUpdateOrderStatus={actions.updateOrderStatus}
            onRequestDriver={(orderId) => {
              const order = state.orders.find((o) => o.id === orderId);
              const gate = assertCanDispatchOrder(order || {});
              if (!gate.ok) {
                toast.error('Despacho bloqueado', gate.message);
                return;
              }
              actions.updateOrderStatus(orderId, 'ready_for_pickup');
            }}
            onLogout={async () => {
              await authService.logout();
              setAuthUserId(null);
              setActiveRole('CLIENT');
            }}
            hasMoreProducts={scopedSupplierDash.hasMore}
            onLoadMoreProducts={() => { void scopedSupplierDash.loadMore(); }}
            productsLoading={scopedSupplierDash.loading}
            catalogTotalCount={scopedSupplierDash.totalCount}
          />
        </div>
      </Suspense>
    );
  }

  // ===== OWNER DASHBOARD (Proprietário/Administrador do Sistema) =====
  if (activeRole === 'OWNER') {
    const cityHallAccount: import('./types').CityHallAccount = {
      id: 'ch_001',
      name: 'Prefeitura de Fortaleza',
      email: 'prefeitura@fortaleza.ce.gov.br',
      employeeId: 'OWNER_001',
      regionalId: 'Regional 1',
      cityHallRole: 'superintendent',
      status: 'active',
      subscriptionStatus: 'active',
      nextPaymentDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    };

    return (
      <Suspense fallback={<DashboardLoader />}>
        <div className={scrollClasses}>
          <OwnerDashboard
            vendors={vendors}
            suppliers={state.suppliers}
            drivers={drivers}
            clients={state.clients}
            orders={state.orders}
            cityHallAccount={cityHallAccount}
            onToggleStatus={(id) => {
              const vendor = vendors.find(v => v.id === id);
              if (vendor) {
                const newStatus = vendor.vendorStatus === 'REGULATED' ? 'SUSPENDED' : 'REGULATED';
                actions.updateVendor({ ...vendor, vendorStatus: newStatus });
              }
            }}
            onToggleDriverStatus={(id) => {
              const targetDriver = drivers.find(d => d.id === id);
              if (targetDriver) {
                actions.updateDriver({
                  ...targetDriver,
                  isOnline: !targetDriver.isOnline,
                  status: (!targetDriver.isOnline ? 'available' : 'offline') as 'available' | 'offline' | 'busy'
                });
              }
            }}
            onToggleCityHallStatus={() => log.info('Toggle City Hall status')}
            onUpdateCityHallPayment={() => log.info('Update City Hall payment')}
            onRefresh={() => window.location.reload()}
            isOffline={false}
            onLogout={async () => { await authService.logout(); setAuthUserId(null); setActiveRole('CLIENT'); }}
            onOpenPartnerCreditPanel={handleOpenPartnerCredit}
            ownerUid={authUserId || undefined}
            onUpdateVendor={(v, options) => actions.updateVendor(v, options)}
            onUpdateSupplier={(s, options) => actions.updateSupplier(s, options)}
            onApproveVendor={(vendorId) => {
              const vendor = vendors.find(v => v.id === vendorId);
              if (vendor) {
                // Owner aprova → vendedor vende imediatamente no app.
                // O TPU/regularização fiscal é processo separado (AGEFIS/Prefeitura)
                // e NÃO bloqueia vendas. O app precisa funcionar e gerar comissão.
                // vendorStatus='REGULATED' = liberado para vender.
                // A situação fiscal real (TPU) fica em regionalVendorAuthorizations,
                // visível apenas no painel dos fiscais e no scanner.
                const vAny = vendor as Vendor & { authUserId?: string; userId?: string; uid?: string };
                const authUid = vAny.authUserId || vAny.uid || vAny.userId || vendorId;
                actions.updateVendor({ ...vendor, vendorStatus: 'REGULATED' });
                void (async () => {
                  try {
                    await setDoc(
                      doc(db, 'users', authUid),
                      { userStatus: 'active', vendorStatus: 'REGULATED', role: 'VENDOR' },
                      { merge: true }
                    );
                    await setDoc(
                      doc(db, 'vendors', vendorId),
                      {
                        vendorStatus: 'REGULATED',
                        authUserId: authUid,
                        uid: authUid,
                      },
                      { merge: true }
                    );
                    toast.success('Vendedor liberado', 'Acesso ao painel ativado.');

                    // P0 — se o geocode do cadastro falhou, Owner re-dispara na aprovação.
                    try {
                      const vSnap = await getDoc(doc(db, 'vendors', vendorId));
                      const locStatus = vSnap.exists()
                        ? String(vSnap.data()?.locationStatus || 'PENDING_ADDRESS')
                        : 'PENDING_ADDRESS';
                      if (locStatus !== 'CONFIRMED') {
                        const geo = await triggerGeocodeVendorAddress(vendorId);
                        if (geo.ok && geo.locationStatus === 'CONFIRMED') {
                          toast.success('Endereço confirmado', 'Loja publicada na vitrine.');
                        } else {
                          toast.warning(
                            'Endereço pendente',
                            'Vendedor aprovado, mas a loja pode não aparecer na vitrine até confirmar o endereço.'
                          );
                        }
                      }
                    } catch (geoErr) {
                      console.error('[Approve] Geocode pós-aprovação falhou:', geoErr);
                      toast.warning(
                        'Endereço pendente',
                        'Vendedor aprovado, mas a loja pode não aparecer na vitrine até confirmar o endereço.'
                      );
                    }
                  } catch (e) {
                    console.error('[Approve] Falha ao liberar vendedor:', e);
                    toast.error(
                      'Falha ao liberar',
                      'Não foi possível atualizar o acesso. Verifique permissões e tente de novo.'
                    );
                  }
                })();
              }
            }}
            onRejectVendor={(vendorId) => {
              const vendor = vendors.find(v => v.id === vendorId);
              if (vendor) {
                actions.updateVendor({ ...vendor, vendorStatus: 'REVOKED' });
                setDoc(doc(db, 'users', vendorId), { userStatus: 'suspended', vendorStatus: 'REVOKED' }, { merge: true })
                  .catch(e => console.error('[Reject] Erro ao atualizar userStatus:', e));
              }
            }}
            onApproveDriver={(driverId) => {
              const driver = drivers.find(d => d.id === driverId);
              if (driver) {
                actions.updateDriver({ ...driver, userStatus: 'active' });
                // Libera acesso do driver: atualiza userStatus no Firestore (users + drivers)
                setDoc(doc(db, 'users', driverId), { userStatus: 'active' }, { merge: true })
                  .catch(e => console.error('[Approve] Erro ao atualizar userStatus driver:', e));
                setDoc(doc(db, 'drivers', driverId), { userStatus: 'active' }, { merge: true })
                  .catch(e => console.error('[Approve] Erro ao atualizar driver doc:', e));
              }
            }}
            onApproveSupplier={(supplierId) => {
              const supplier = state.suppliers.find(s => s.id === supplierId || s.uid === supplierId);
              if (supplier) {
                actions.updateSupplier({ ...supplier, supplierStatus: 'active' });
                // Libera acesso do supplier: atualiza userStatus para 'active' no Firestore
                setDoc(doc(db, 'users', supplierId), { userStatus: 'active' }, { merge: true })
                  .catch(e => console.error('[Approve] Erro ao atualizar userStatus supplier:', e));
              }
            }}
            onRejectSupplier={(supplierId) => {
              const supplier = state.suppliers.find(s => s.id === supplierId || s.uid === supplierId);
              if (supplier) {
                actions.updateSupplier({ ...supplier, supplierStatus: 'suspended' });
                setDoc(doc(db, 'users', supplierId), { userStatus: 'suspended' }, { merge: true })
                  .catch(e => console.error('[Reject] Erro ao atualizar userStatus supplier:', e));
              }
            }}
            onApproveCityHall={(accountId) => {
              setDoc(doc(db, 'users', accountId), { userStatus: 'active' }, { merge: true })
                .catch(e => console.error('[Approve] Erro users CITY_HALL:', e));
              setDoc(doc(db, 'cityHallAccounts', accountId), { status: 'active' }, { merge: true })
                .catch(e => console.error('[Approve] Erro cityHallAccounts:', e));
            }}
            onRejectCityHall={(accountId) => {
              // Suspensão temporária — fiscal pode ser reativado
              setDoc(doc(db, 'users', accountId), { userStatus: 'suspended' }, { merge: true })
                .catch(e => console.error('[Suspend] Erro users CITY_HALL:', e));
              setDoc(doc(db, 'cityHallAccounts', accountId), { status: 'suspended' }, { merge: true })
                .catch(e => console.error('[Suspend] Erro cityHallAccounts:', e));
            }}
            onRevokeCityHall={(accountId) => {
              // Revogação definitiva — demissão
              setDoc(doc(db, 'users', accountId), { userStatus: 'revoked' }, { merge: true })
                .catch(e => console.error('[Revoke] Erro users CITY_HALL:', e));
              setDoc(doc(db, 'cityHallAccounts', accountId), { status: 'revoked' }, { merge: true })
                .catch(e => console.error('[Revoke] Erro cityHallAccounts:', e));
            }}
          />
        </div>
      </Suspense>
    );
  }

  // Visitante: prévia Ágora em tela dedicada (sem shell do cliente, sem lazy do chunk Ágora)
  if (!authUserId && clientView === 'agora') {
    return (
      <div className="min-h-screen">
        <PublicAgoraPage
          isAuthenticated={false}
          onBack={handleCloseAgoraPreview}
          onExplore={() => setClientView('directory')}
          onRegisterClick={() => setIsRegisterModalOpen(true)}
          onLoginClick={() => setIsLoginModalOpen(true)}
        />

        <Suspense fallback={null}>
          <LoginModal
            isOpen={isLoginModalOpen}
            onClose={() => setIsLoginModalOpen(false)}
            onLogin={handleLoginSubmit}
            onRegister={() => { setIsLoginModalOpen(false); setIsRegisterModalOpen(true); }}
            email={loginEmail}
            setEmail={setLoginEmail}
            password={loginPassword}
            setPassword={setLoginPassword}
            isLoading={isLoggingIn}
          />
        </Suspense>

        <RegisterModalOverlay
          isOpen={isRegisterModalOpen}
          onClose={() => setIsRegisterModalOpen(false)}
        >
          <Suspense fallback={<DashboardLoader />}>
            <RegistrationScreen
              onRegister={handleRegister}
              onNavigateToLogin={() => {
                setIsRegisterModalOpen(false);
                setIsLoginModalOpen(true);
              }}
              onClose={() => setIsRegisterModalOpen(false)}
            />
          </Suspense>
        </RegisterModalOverlay>
        <Suspense fallback={null}>
          <AIAssistant 
            language={language}
            context={{
              totalVendors: vendors.filter(v => 
                !suppliers.some(s => s.id === v.id) && 
                v.vendorStatus !== 'SUSPENDED' && 
                v.vendorStatus !== 'PENDING_VERIFICATION'
              ).length,
              totalSuppliers: suppliers.length,
              vendorsByCategory: {
                comida: vendorsByCategory['comida']?.length || 0,
                moda: vendorsByCategory['moda']?.length || 0,
                artesanato: vendorsByCategory['artesanato']?.length || 0,
                presentes: vendorsByCategory['presentes']?.length || 0,
                mobilidade: vendorsByCategory['mobilidade']?.length || 0,
                fitness: vendorsByCategory['fitness']?.length || 0,
                servicos: vendorsByCategory['servicos']?.length || 0,
                outros: vendorsByCategory['outros']?.length || 0,
              },
              isUserLoggedIn: !!authUserId,
              userRole: activeRole,
            }}
            onNavigate={(action) => {
              if (action.type === 'navigate_category' && action.categoryId) {
                setSelectedCategory(action.categoryId);
                setClientView('directory');
              } else if (action.type === 'navigate_directory') {
                setSelectedCategory(null);
                setClientView('directory');
              } else if (action.type === 'navigate_login') {
                setIsLoginModalOpen(true);
              } else if (action.type === 'navigate_register') {
                setIsRegisterModalOpen(true);
              }
            }}
          />
        </Suspense>
      </div>
    );
  }

  // Se não está logado e está na home, mostra a LandingPage premium
  if (!authUserId && clientView === 'home') {
    return (
      <div className="min-h-screen">
        <Suspense fallback={<DashboardLoader />}>
          <LandingPage
            onExplore={() => setClientView('directory')}
            onCategoryClick={(categoryId) => {
              setSelectedCategory(categoryId);
              setClientView('directory');
            }}
            onRegisterClick={() => setIsRegisterModalOpen(true)}
            onLoginClick={() => setIsLoginModalOpen(true)}
            onAgoraClick={handleOpenAgoraPreview}
            onCreditPreviewClick={handleOpenCreditPreview}
          />
        </Suspense>

        {/* Modal de Login */}
        <Suspense fallback={null}>
          <LoginModal
            isOpen={isLoginModalOpen}
            onClose={() => setIsLoginModalOpen(false)}
            onLogin={handleLoginSubmit}
            onRegister={() => { setIsLoginModalOpen(false); setIsRegisterModalOpen(true); }}
            email={loginEmail}
            setEmail={setLoginEmail}
            password={loginPassword}
            setPassword={setLoginPassword}
            isLoading={isLoggingIn}
          />
        </Suspense>

        {/* Modal de Cadastro */}
        <RegisterModalOverlay
          isOpen={isRegisterModalOpen}
          onClose={() => setIsRegisterModalOpen(false)}
        >
          <Suspense fallback={<DashboardLoader />}>
            <RegistrationScreen
              onRegister={handleRegister}
              onNavigateToLogin={() => {
                setIsRegisterModalOpen(false);
                setIsLoginModalOpen(true);
              }}
              onClose={() => setIsRegisterModalOpen(false)}
            />
          </Suspense>
        </RegisterModalOverlay>
        <Suspense fallback={null}>
          <AIAssistant 
            language={language}
            context={{
              totalVendors: vendors.filter(v => 
                !suppliers.some(s => s.id === v.id) && 
                v.vendorStatus !== 'SUSPENDED' && 
                v.vendorStatus !== 'PENDING_VERIFICATION'
              ).length,
              totalSuppliers: suppliers.length,
              vendorsByCategory: {
                comida: vendorsByCategory['comida']?.length || 0,
                moda: vendorsByCategory['moda']?.length || 0,
                artesanato: vendorsByCategory['artesanato']?.length || 0,
                presentes: vendorsByCategory['presentes']?.length || 0,
                mobilidade: vendorsByCategory['mobilidade']?.length || 0,
                fitness: vendorsByCategory['fitness']?.length || 0,
                servicos: vendorsByCategory['servicos']?.length || 0,
                outros: vendorsByCategory['outros']?.length || 0,
              },
              isUserLoggedIn: !!authUserId,
              userRole: activeRole,
            }}
            onNavigate={(action) => {
              if (action.type === 'navigate_category' && action.categoryId) {
                setSelectedCategory(action.categoryId);
                setClientView('directory');
              } else if (action.type === 'navigate_directory') {
                setSelectedCategory(null);
                setClientView('directory');
              } else if (action.type === 'navigate_login') {
                setIsLoginModalOpen(true);
              } else if (action.type === 'navigate_register') {
                setIsRegisterModalOpen(true);
              }
            }}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-slate-50 flex flex-col ${scrollClasses} max-w-md mx-auto sm:max-w-full`}>
       {/* NAVEGAÇÃO OTIMIZADA PARA MOBILE */}
       <nav className="bg-white/80 backdrop-blur-md p-3 shadow-sm flex justify-between items-center sticky top-0 z-50 border-b border-slate-100">
                    {activeRole === 'VENDOR' && authUserId && (
                      <button
                        onClick={() => setClientView('suppliers')}
                        className="flex flex-col items-center gap-0.5 text-xs font-medium
                                   text-slate-400 hover:text-brand-green transition-colors"
                      >
                        <span className="text-lg">🏪</span>
                        <span>Fornecedores</span>
                      </button>
                    )}
          <div className="flex items-center gap-1.5 font-black text-brand-green text-lg cursor-pointer hover:scale-105 transition-transform" onClick={() => {setClientView('home'); setAudioEnabled(true);}}>
             <ShoppingBag className="text-brand-green w-5 h-5" /> 
             <span className="hidden xs:block">{APP_NAME}</span>
             <span className="xs:hidden text-sm">E-Amb</span>
          </div>
          <div className="flex items-center gap-2">
             {cart.length > 0 && (
               <button onClick={() => setIsCartOpen(true)} className="relative p-2 bg-slate-100 rounded-xl text-slate-800 transition-all hover:bg-brand-green hover:text-white touch-manipulation">
                  <ShoppingBag size={18} />
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold border border-white shadow-sm">{cart.length}</span>
               </button>
             )}
             {isCheckingAuth ? (
               /* Skeleton neutro enquanto verifica sessão - evita flicker do ?cone */
               <div className="w-8 h-8 rounded-xl bg-slate-200 animate-pulse" />
             ) : authUserId ? (
               <>
                 <Suspense fallback={<div className="w-8 h-8 rounded-xl bg-slate-200 animate-pulse" />}>
                   <AccountSettings />
                 </Suspense>
                 <button 
                   onClick={async () => { await authService.logout(); setAuthUserId(null); }}
                   className="p-2 bg-slate-100 rounded-xl text-slate-600 hover:bg-red-100 hover:text-red-600 transition-all touch-manipulation"
                   title="Sair"
                 >
                   <LogOut size={18} />
                 </button>
               </>
             ) : (
               <button
                 onClick={() => setIsLoginModalOpen(true)}
                 className="w-9 h-9 bg-slate-100 hover:bg-emerald-100 hover:text-emerald-700 text-slate-500 rounded-xl flex items-center justify-center transition-all touch-manipulation"
                 title="Entrar"
               >
                 <User size={20} />
               </button>
             )}
          </div>
       </nav>
       
       <div className="flex-1">
                    {clientView === 'suppliers' && activeRole === 'VENDOR' && (
                      <div className="p-4 space-y-4 animate-in fade-in slide-in-from-bottom-4">
                        {/* Header */}
                        <div className="flex items-center gap-3 bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
                          <button
                            onClick={() => setClientView('directory')}
                            className="p-2 bg-slate-50 rounded-xl text-slate-400
                                       hover:text-brand-green transition-all touch-manipulation"
                          >
                            <ArrowLeft size={18} />
                          </button>
                          <div>
                            <h1 className="font-black text-slate-800 text-base">Fornecedores</h1>
                            <p className="text-slate-400 text-xs">
                              Compre estoque para sua loja
                            </p>
                          </div>
                        </div>

                        {/* Lista de Suppliers */}
                        {suppliers.length === 0 ? (
                          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                            <p className="text-5xl mb-4">📦</p>
                            <p className="text-slate-700 font-bold text-base">
                              Nenhum fornecedor disponível
                            </p>
                            <p className="text-slate-400 text-sm mt-2">
                              Em breve novos parceiros chegarão!
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {suppliers.map(supplier => {
                              const supplierOpen = isStoreOpen(supplier);
                              return (
                              <div
                                key={supplier.id}
                                onClick={() => {
                                  setActiveSupplierId(supplier.id);
                                  setClientView('supplier-store');
                                }}
                                className="bg-white rounded-2xl shadow-sm border border-slate-100
                                           hover:border-brand-green/40 hover:shadow-md
                                           transition-all cursor-pointer overflow-hidden
                                           touch-manipulation"
                              >
                                {/* Banner */}
                                <div className="relative h-32">
                                  <img
                                    src={resolveSupplierImage(supplier, 'card', 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400')}
                                    alt={supplier.companyName}
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                  <div className="absolute bottom-3 left-3 text-white">
                                    <p className="font-black text-sm leading-tight">
                                      {supplier.tradeName || supplier.companyName}
                                    </p>
                                    {supplier.category && (
                                      <p className="text-white/75 text-xs mt-0.5 capitalize">
                                        {supplier.category}
                                      </p>
                                    )}
                                  </div>
                                  <div className="absolute top-2 right-2">
                                    <span className={`text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                                      supplierOpen ? 'bg-emerald-500' : 'bg-amber-500'
                                    }`}>
                                      <span className={`w-1.5 h-1.5 rounded-full bg-white ${supplierOpen ? 'animate-pulse' : ''}`} />
                                      {supplierOpen ? 'Aberto' : 'Fechado'}
                                    </span>
                                  </div>
                                </div>
                                <div className="p-3 flex items-center justify-between">
                                  <div className="flex items-center gap-3 text-xs text-slate-500">
                                    {supplier.minimumOrder && (
                                      <span>Mín. R$ {formatBRLNumber(supplier.minimumOrder)}</span>
                                    )}
                                    {supplier.deliveryFee !== undefined && (
                                      <span>Frete R$ {formatBRLNumber(supplier.deliveryFee)}</span>
                                    )}
                                  </div>
                                  <span className="text-brand-green text-xs font-bold">
                                    Ver catálogo →
                                  </span>
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {clientView === 'supplier-store' && activeSupplierId && (activeRole === 'VENDOR' || activeRole === 'CLIENT' || !activeRole) && (() => {
                      const activeSupplier = suppliers.find(s => s.id === activeSupplierId);
                      if (state.isLoading) {
                        return <DashboardLoader />;
                      }
                      if (!activeSupplier) {
                        return null;
                      }
                      return (
                        <Suspense fallback={<DashboardLoader />}>
                          <SupplierStorePage
                            supplier={activeSupplier}
                            isStoreOpen={isStoreOpen(activeSupplier)}
                            products={supplierStoreCatalog.products}
                            cartItemsCount={cart.length}
                            language={language}
                            onBack={() => {
                              setActiveSupplierId(null);
                              setClientView(activeRole === 'VENDOR' ? 'suppliers' : 'directory');
                            }}
                            onAddToCart={handleAddToCartClick}
                            onOpenCart={() => setIsCartOpen(true)}
                            catalog={supplierStoreCatalog}
                          />
                        </Suspense>
                      );
                    })()}
          {clientView === 'agora' && (
            <PublicAgoraPage
              isAuthenticated={!!authUserId}
              onBack={handleCloseAgoraPreview}
              onExplore={() => setClientView('directory')}
              onRegisterClick={() => setIsRegisterModalOpen(true)}
              onLoginClick={() => setIsLoginModalOpen(true)}
            />
          )}
          {clientView === 'home' && (
            <Suspense fallback={<DashboardLoader />}>
              <LandingPage 
                onExplore={() => setClientView('directory')} 
                onCategoryClick={(categoryId) => {
                  setSelectedCategory(categoryId);
                  setClientView('directory');
                }}
                onRegisterClick={() => setIsRegisterModalOpen(true)}
                onLoginClick={() => setIsLoginModalOpen(true)}
                onAgoraClick={handleOpenAgoraPreview}
                onCreditPreviewClick={handleOpenCreditPreview}
              />
            </Suspense>
          )}
          {clientView === 'directory' && (
            <div className="pb-8 animate-in fade-in slide-in-from-bottom-4">
              {/* CABEÇALHO ?? */}
              <div className="sticky top-0 z-20 bg-slate-50/95 backdrop-blur-sm border-b border-slate-200/60 px-4 pt-4 pb-3 space-y-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => { setClientView('home'); setSelectedCategory(null); }}
                    className="p-2 bg-white rounded-xl text-slate-400 hover:text-brand-green shadow-sm border border-slate-200 transition-all touch-manipulation"
                  >
                    <ArrowLeft size={18}/>
                  </button>
                  <div className="flex-1">
                    <h2 className="text-lg font-black text-slate-800 tracking-tight">
                      {selectedCategory ? getCategoryName(selectedCategory) : 'Explorar Ambulantes'}
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {openVendorCount > 0
                        ? `${openVendorCount} aberto${openVendorCount !== 1 ? 's' : ''} · ${filteredVendors.length} na vitrine`
                        : `${filteredVendors.length} loja${filteredVendors.length !== 1 ? 's' : ''} na sua região`}
                    </p>
                  </div>
                </div>
                {/* FILTRO DE CATEGORIAS ?? */}
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide -mx-1 px-1">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      !selectedCategory
                        ? 'bg-emerald-700 text-white border-emerald-700 shadow-sm'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    Todos
                  </button>
                  {VENDOR_CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        selectedCategory === cat.id
                          ? 'bg-emerald-700 text-white border-emerald-700 shadow-sm'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <span className="flex items-center justify-center">{cat.icon}</span>
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* CONTEÚDO ?? */}
              <div className="px-4 mt-4 space-y-6">
                {(state.isLoading || (state.syncStatus !== 'synced' && state.vendors.length === 0)) ? (
                  <div className="text-center py-16 bg-white rounded-2xl">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-green/10 rounded-full mb-4">
                      <div className="w-8 h-8 border-4 border-brand-green border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <p className="text-slate-600 font-bold text-lg">Carregando ambulantes...</p>
                    <p className="text-slate-400 text-sm mt-1">Aguarde um momento</p>
                  </div>
                ) : selectedCategory ? (
                  /* CATEGORIA FILTRADA ?? */
                  filteredVendors.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl">
                      <p className="text-4xl mb-3 flex justify-center">{VENDOR_CATEGORIES.find(c => c.id === selectedCategory)?.icon || <Package size={36} strokeWidth={2} />}</p>
                      <p className="text-slate-600 font-semibold">Nenhum ambulante nesta categoria ainda.</p>
                      <button onClick={() => setSelectedCategory(null)} className="mt-4 text-brand-green font-bold text-sm underline">
                        Ver todas as categorias
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {filteredVendors.map(v => <VendorCard key={v.id} v={v} onSelect={handleSelectVendor} />)}
                    </div>
                  )
                ) : (
                  /* TODAS AS CATEGORIAS AGRUPADAS ?? */
                  <>
                    {VENDOR_CATEGORIES.filter(cat => (vendorsByCategory[cat.id] || []).length > 0).map(cat => (
                      <section key={cat.id}>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                            <span className="text-lg flex items-center justify-center">{cat.icon}</span>
                            {cat.label}
                            <span className="text-xs font-normal text-slate-400">({(vendorsByCategory[cat.id] || []).length})</span>
                          </h3>
                          {(vendorsByCategory[cat.id] || []).length > 4 && (
                            <button onClick={() => setSelectedCategory(cat.id)} className="text-xs text-emerald-700 font-semibold hover:underline">
                              Ver todos
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {(vendorsByCategory[cat.id] || []).slice(0, 4).map(v => <VendorCard key={v.id} v={v} onSelect={handleSelectVendor} />)}
                        </div>
                      </section>
                    ))}

                    {/* SEÇÃO ATACADISTAS / FORNECEDORES ?? */}
                    {publicSuppliers.length > 0 && (
                      <section className="mt-2">
                        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-4 mb-4 text-white">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl"><Factory size={24} strokeWidth={2} /></span>
                            <div>
                              <h3 className="font-bold text-base leading-tight">Atacado & Fornecedores</h3>
                              <p className="text-slate-300 text-xs">Compre direto do distribuidor</p>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {publicSuppliers.map(s => {
                            const supplierOpen = isStoreOpen(s);
                            return (
                            <SupplierCard
                              key={s.id}
                              s={s}
                              isOpen={supplierOpen}
                              onSelect={(id) => {
                                setActiveSupplierId(id);
                                setClientView('supplier-store');
                              }}
                            />
                          );
                          })}
                        </div>
                      </section>
                    )}

                    {/* ESTADO VAZIO ?? */}
                    {Object.keys(vendorsByCategory).length === 0 && publicSuppliers.length === 0 && (
                      <div className="text-center py-16 bg-white rounded-2xl">
                        <p className="text-5xl mb-4">📦</p>
                        <p className="text-slate-600 font-semibold">Nenhum ambulante cadastrado ainda.</p>
                        <p className="text-slate-400 text-sm mt-1">Volte em breve!</p>
                      </div>
                    )}
                  </>
                )}
              </div>

            </div>
          )}
          {clientView === 'store' && activeVendorId && (() => {
            const storeVendor = vendors.find((v) => v.id === activeVendorId);
            if (!storeVendor) {
              return (
                <div className="max-w-md mx-auto mt-16 p-8 bg-white rounded-3xl border border-slate-200 text-center shadow-sm">
                  <AlertTriangle className="mx-auto text-amber-500 mb-4" size={40} />
                  <h2 className="text-lg font-black text-slate-800 mb-2">Loja não encontrada</h2>
                  <Button onClick={() => setClientView('directory')}>Voltar ao diretório</Button>
                </div>
              );
            }
            return (
            <Suspense fallback={<DashboardLoader />}>
              <VendorStorePage 
                  vendor={storeVendor}
                  isStoreOpen={isVendorStoreLive(storeVendor)}
                  products={vendorStoreCatalog.products} 
                  cartItemsCount={cart.length}
                  cartQuantity={cart.reduce((total, item) => total + item.quantity, 0)}
                  cartSubtotal={cart.reduce((total, item) => total + item.price * item.quantity, 0)}
                  language={language} 
                  onBack={() => {
                    setActiveVendorId(null);
                    setClientView('home');
                  }} 
                  onAddToCart={handleAddToCartClick} 
                  onOpenCart={() => setIsCartOpen(true)}
                  catalog={vendorStoreCatalog}
              />
            </Suspense>
            );
          })()}
       </div>
       
       {/* Obtêm dados do vendedor do carrinho para o checkout */}
       {(() => {
         const currentCartVendorId = cart[0]?.vendorId;
         const cartVendor = currentCartVendorId
           ? (vendors.find(v => v.id === currentCartVendorId) ?? null)
           : null;
         const cartSupplier = (!cartVendor && currentCartVendorId)
           ? (suppliers.find(s => s.id === currentCartVendorId) ?? null)
           : null;
         const cartPhone = cartVendor?.phone || cartSupplier?.phone;
         const cartPixKey =
           getConfirmedPixDisplayKey(cartVendor?.payment) ||
           getConfirmedPixDisplayKey(cartSupplier?.payment);
         const cartLocation = cartVendor?.location ?? undefined;
         return isCartOpen ? (
           <Suspense fallback={null}>
             <CartDrawer 
                  isOpen={isCartOpen} 
                  onClose={() => setIsCartOpen(false)} 
                  items={cart} 
                  onAdd={(id) => setCart(cart.map(i => i.id === id ? { ...i, quantity: i.quantity + 1 } : i))} 
                  onRemove={(id) => setCart(cart.map(i => i.id === id && i.quantity > 1 ? { ...i, quantity: i.quantity - 1 } : i))} 
                  onClear={() => { setCart([]); setCartVendorId(null); clearCartSession(); }} 
                  onOrderCreated={(order) => {
                    setCart([]);
                    setCartVendorId(null);
                    clearCartSession();
                    setOrderSuccess({ show: true, order });
                  }}
                  deliveryFee={DELIVERY_CONFIG.BASE_FEE}
                  vendorPixKey={cartPixKey}
                  vendorPhone={cartPhone}
                  vendorLocation={cartLocation}
             />
           </Suspense>
         ) : null;
       })()}
       
       {customizingProduct && (
         <Suspense fallback={null}>
           <ProductCustomizationModal isOpen={!!customizingProduct} onClose={() => setCustomizingProduct(null)} product={customizingProduct} onConfirm={handleConfirmCustomization} language={language} />
         </Suspense>
       )}
       
       {/* OrderSuccess com dados do vendedor */}
       {orderSuccess.show && orderSuccess.order && (() => {
         const orderVendor = vendors.find(v => v.id === orderSuccess.order?.vendorId);
         const orderSupplier = suppliers.find(s => s.id === orderSuccess.order?.vendorId);
         const successPixKey =
           getConfirmedPixDisplayKey(orderVendor?.payment) ||
           getConfirmedPixDisplayKey(orderSupplier?.payment);
         return (
           <Suspense fallback={null}>
             <OrderSuccess 
               order={orderSuccess.order} 
               onClose={() => { setOrderSuccess({ show: false, order: null }); setClientView('home'); }}
              onGoToLogin={(email) => {
                setOrderSuccess({ show: false, order: null });
                if (email) setLoginEmail(email);
                setIsLoginModalOpen(true);
              }}
               vendorPhone={orderVendor?.phone || orderSupplier?.phone}
               vendorName={orderVendor?.storeName || orderVendor?.name || orderSupplier?.tradeName || orderSupplier?.companyName}
               vendorPixKey={successPixKey}
               vendorPixPayload={getPixPayloadKey(orderVendor?.payment) || getPixPayloadKey(orderSupplier?.payment)}
              isAnonymousClient={!authUserId && !orderSuccess.order?.clientUid}
             />
           </Suspense>
         );
       })()}

       {ratingModal.show && ratingModal.order && (
         <Suspense fallback={null}>
           <RatingModal isOpen={ratingModal.show} order={ratingModal.order} onClose={() => setRatingModal({ show: false, order: null })} onSubmit={(rating, comment) => { console.log('Avaliacao:', { orderId: ratingModal.order?.id, rating, comment }); setRatingModal({ show: false, order: null }); }} />
         </Suspense>
       )}
       
       {/* MODAL DE LOGIN com Recuperação de Senha */}
       <Suspense fallback={null}>
         <LoginModal
            isOpen={isLoginModalOpen}
            onClose={() => setIsLoginModalOpen(false)}
            onLogin={handleLoginSubmit}
            onRegister={() => { setIsLoginModalOpen(false); setIsRegisterModalOpen(true); }}
            email={loginEmail}
            setEmail={setLoginEmail}
            password={loginPassword}
            setPassword={setLoginPassword}
            isLoading={isLoggingIn}
         />
       </Suspense>
       
       {/* MODAL DE CADASTRO */}
       <RegisterModalOverlay
         isOpen={isRegisterModalOpen}
         onClose={() => setIsRegisterModalOpen(false)}
       >
         <Suspense fallback={<DashboardLoader />}>
           <RegistrationScreen
             onRegister={handleRegister}
             onNavigateToLogin={() => {
               setIsRegisterModalOpen(false);
               setIsLoginModalOpen(true);
             }}
             onClose={() => setIsRegisterModalOpen(false)}
           />
         </Suspense>
       </RegisterModalOverlay>
       <Suspense fallback={null}>
         <AIAssistant 
            language={language}
            context={{
              totalVendors: vendors.filter(v => 
                !suppliers.some(s => s.id === v.id) && 
                v.vendorStatus !== 'SUSPENDED' && 
                v.vendorStatus !== 'PENDING_VERIFICATION'
              ).length,
              totalSuppliers: suppliers.length,
              vendorsByCategory: {
                comida: vendorsByCategory['comida']?.length || 0,
                moda: vendorsByCategory['moda']?.length || 0,
                artesanato: vendorsByCategory['artesanato']?.length || 0,
                presentes: vendorsByCategory['presentes']?.length || 0,
                mobilidade: vendorsByCategory['mobilidade']?.length || 0,
                fitness: vendorsByCategory['fitness']?.length || 0,
                servicos: vendorsByCategory['servicos']?.length || 0,
                outros: vendorsByCategory['outros']?.length || 0,
              },
              isUserLoggedIn: !!authUserId,
              userRole: activeRole,
            }}
            onNavigate={(action) => {
              if (action.type === 'navigate_category' && action.categoryId) {
                setSelectedCategory(action.categoryId);
                setClientView('directory');
              } else if (action.type === 'navigate_directory') {
                setSelectedCategory(null);
                setClientView('directory');
              } else if (action.type === 'navigate_login') {
                setIsLoginModalOpen(true);
              } else if (action.type === 'navigate_register') {
                setIsRegisterModalOpen(true);
              }
            }}
          />
       </Suspense>
    </div>
  );
}

